import React, { useEffect, useState } from "react";
import axios from "axios";
import { getApiUrl } from "@/utils/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ColorPickerInput } from "@/components/ui/ColorPickerInput";
import { CTA_ICONS, getCtaIcon } from "@/components/cms/ctaIcons";
import HeadingLevelSelect from "@/components/ui/HeadingLevelSelect";
import TextStyleControls from "@/components/ui/TextStyleControls";
import { AlignmentToggle } from "@/components/ui/AlignmentToggle";
import { ImageUpload } from "@/components/ImageUpload";
import { resolveFontSize, DEFAULT_HEADING_LEVEL } from "@/utils/headingSize";
import { resolveImageUrl } from "@/utils/imageUrl";
import { GRADIENT_DIRECTIONS, buildGradientCss } from "@/utils/gradientOverlay";
import { useSiteStyles } from "@/hooks/useSiteStyles";
import { TiptapEditor } from "@/components/admin/TiptapEditor";
import PropertyArticleConfigFields from "@/components/cms/PropertyArticleConfigFields";
import { pollAiJob } from "@/utils/pollAiJob";
import { Trash2, Copy, ExternalLink, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = getApiUrl();

const LOGO_IMAGE_SHADOW_PRESETS = ["none", "soft", "medium", "strong"];

/**
 * Shared logo-image size/border/shadow controls — used by both the Header
 * module's Logo Image field and the Navigation module's optional "Show
 * logo" block. Field names are namespaced under `logo_image_*` (distinct
 * from the `logo_*` prefix TextStyleControls already uses for the logo
 * TEXT's font/color) so both sets of config live side by side without
 * collisions. Rendering side lives in ModularPage.jsx's `logoImageStyle()`.
 */
function LogoImageStyleControls({ configFields, update }) {
  const borderWidth = Number(configFields.logo_image_border_width || 0);
  return (
    <div className="space-y-3 pt-2 border-t">
      <Label className="text-[11px] font-medium">Logo Image Size & Style</Label>
      <div className="space-y-1">
        <Label className="text-[11px]">Size (height): {configFields.logo_image_height || 48}px</Label>
        <input
          type="range" min="24" max="120" step="2"
          value={configFields.logo_image_height || 48}
          onChange={(e) => update({ logo_image_height: parseInt(e.target.value) })}
          className="w-full"
          data-testid="logo-image-height-slider"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-[11px]">Border width: {borderWidth}px</Label>
          <input
            type="range" min="0" max="10" step="0.5"
            value={borderWidth}
            onChange={(e) => update({ logo_image_border_width: parseFloat(e.target.value) })}
            className="w-full"
            data-testid="logo-image-border-width-slider"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-[11px]">Corner radius: {configFields.logo_image_border_radius ?? 0}px</Label>
          <input
            type="range" min="0" max="60" step="2"
            value={configFields.logo_image_border_radius ?? 0}
            onChange={(e) => update({ logo_image_border_radius: parseInt(e.target.value) })}
            className="w-full"
            data-testid="logo-image-border-radius-slider"
          />
          <p className="text-[10px] text-muted-foreground">Half the height makes a circular logo</p>
        </div>
      </div>
      {borderWidth > 0 && (
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-[11px]">Border style</Label>
            <select
              value={configFields.logo_image_border_style || "solid"}
              onChange={(e) => update({ logo_image_border_style: e.target.value })}
              className="w-full h-9 px-3 border border-input rounded text-sm bg-background"
              data-testid="logo-image-border-style-select"
            >
              <option value="solid">Solid</option>
              <option value="dashed">Dashed</option>
              <option value="dotted">Dotted</option>
            </select>
          </div>
          <ColorPickerInput
            label="Border color" compact
            value={configFields.logo_image_border_color || "#e5e7eb"}
            onChange={(v) => update({ logo_image_border_color: v })}
          />
        </div>
      )}
      <div className="space-y-1">
        <Label className="text-[11px]">Shadow</Label>
        <div className="flex gap-1.5">
          {LOGO_IMAGE_SHADOW_PRESETS.map((key) => (
            <Button
              key={key}
              type="button"
              size="sm"
              variant={(configFields.logo_image_shadow || "none") === key ? "default" : "outline"}
              onClick={() => update({ logo_image_shadow: key })}
              className="text-xs capitalize flex-1"
              data-testid={`logo-image-shadow-${key}`}
            >
              {key}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * TextModuleConfig — Prompt + AI CTA + Default HTML Content editor.
 *
 * Extracted as a top-level component so its `useState` for the "generating"
 * spinner satisfies React's rules-of-hooks (the parent switches between
 * module types, so a conditional `useState` inside `ModuleConfigFields`
 * would break the hook order between renders).
 */
function TextModuleConfig({ configFields, update, allPages = { modular: [], preset: [], custom: [] } }) {
  const [aiGenerating, setAiGenerating] = useState(false);

  const runAiGenerate = async () => {
    const prompt = (configFields.ai_prompt || "").trim();
    if (!prompt) {
      toast.error("Enter a prompt first");
      return;
    }
    setAiGenerating(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/cms/ai-text`,
        { prompt, existing_html: configFields.content || "" },
        { withCredentials: true },
      );
      const result = await pollAiJob(data.job_id);
      const html = (result?.html || "").trim();
      if (!html) {
        toast.error("AI returned no content");
        return;
      }
      // Push into the module config — parent autosave picks it up as soon
      // as this state change flushes through React.
      update({ content: html });
      toast.success("AI content added to Default HTML");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "AI generation failed");
    } finally {
      setAiGenerating(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2 rounded-md border border-primary/30 bg-primary/[0.03] p-3">
        <div className="flex items-center justify-between gap-2">
          <Label htmlFor="text-ai-prompt" className="text-sm font-semibold flex items-center gap-1.5">
            <Sparkles className="h-3.5 w-3.5 text-primary" />
            Prompt
          </Label>
          <Button
            type="button"
            size="sm"
            onClick={runAiGenerate}
            disabled={aiGenerating}
            className="gap-1.5"
            data-testid="text-module-ai-generate"
          >
            {aiGenerating ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Sparkles className="h-3.5 w-3.5" />
            )}
            AI
          </Button>
        </div>
        <Textarea
          id="text-ai-prompt"
          value={configFields.ai_prompt || ""}
          onChange={(e) => update({ ai_prompt: e.target.value })}
          placeholder="e.g. Write a warm 2-paragraph intro for our About Us page, mention we're a local family-run agency in Kent."
          rows={3}
          data-testid="text-module-ai-prompt"
        />
        <p className="text-[11px] text-muted-foreground">
          Click AI to generate HTML content from this prompt. Any content
          currently in the field is offered to the model as context; the
          generated HTML replaces it in the field below (still editable).
        </p>
      </div>
      <div className="space-y-2">
        <Label>Default HTML Content</Label>
        <div className="border rounded-md">
          <TiptapEditor
            value={configFields.content || ""}
            onChange={(html) => update({ content: html })}
            placeholder="Start typing your content here..."
          />
        </div>
        <p className="text-xs text-muted-foreground">
          Use the toolbar above to format text, add headings, images, links, and more.
        </p>
      </div>

      <div className="p-3 bg-slate-50 rounded-lg border space-y-3">
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="text_cta_enabled_toggle"
            checked={!!configFields.cta_enabled}
            onChange={(e) => update({ cta_enabled: e.target.checked })}
            data-testid="text-cta-enabled-checkbox"
          />
          <Label htmlFor="text_cta_enabled_toggle" className="font-semibold text-sm">
            Call-to-Action Button
          </Label>
        </div>
        {configFields.cta_enabled && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>CTA Text</Label>
                <Input
                  placeholder="Learn More"
                  value={configFields.cta_text || ""}
                  onChange={(e) => update({ cta_text: e.target.value })}
                  data-testid="text-cta-text-input"
                />
              </div>
              <div className="space-y-2">
                <Label>CTA Position</Label>
                <select
                  data-testid="text-cta-position-select"
                  value={configFields.cta_position || "bottom"}
                  onChange={(e) => update({ cta_position: e.target.value })}
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="top">Top of content</option>
                  <option value="bottom">Bottom of content</option>
                  <option value="both">Both top and bottom</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label>CTA Alignment</Label>
                <select
                  value={configFields.cta_align || "center"}
                  onChange={(e) => update({ cta_align: e.target.value })}
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="left">Left</option>
                  <option value="center">Center</option>
                  <option value="right">Right</option>
                </select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Link To Page</Label>
              <select
                data-testid="text-cta-link-page-select"
                value={configFields.cta_link || ""}
                onChange={(e) => update({ cta_link: e.target.value })}
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="">Select a page…</option>
                {allPages.modular?.length > 0 && (
                  <optgroup label="Modular Pages">
                    {allPages.modular.map((p) => (
                      <option
                        key={p.composition_id}
                        value={p.page_slug === null ? "/" : (p.page_slug ? `/${p.page_slug}` : "/")}
                      >
                        {p.page_title} ({p.page_slug === null ? "/" : `/${p.page_slug}`})
                      </option>
                    ))}
                  </optgroup>
                )}
                {allPages.preset?.length > 0 && (
                  <optgroup label="Preset Pages">
                    {allPages.preset.map((p) => (
                      <option key={p.nav_id} value={p.path}>
                        {p.title} ({p.path})
                      </option>
                    ))}
                  </optgroup>
                )}
                {allPages.custom?.length > 0 && (
                  <optgroup label="Custom Pages">
                    {allPages.custom.map((p) => (
                      <option key={p.page_id} value={`/${p.slug}`}>
                        {p.title} (/{p.slug})
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <ColorPickerInput
                label="CTA Background"
                compact
                value={configFields.cta_bg_color || "#2f4f4f"}
                onChange={(v) => update({ cta_bg_color: v })}
              />
              <ColorPickerInput
                label="CTA Text Color"
                compact
                value={configFields.cta_text_color || "#ffffff"}
                onChange={(v) => update({ cta_text_color: v })}
              />
            </div>
            <div className="space-y-2">
              <Label>Corner Radius: {configFields.cta_border_radius || 8}px</Label>
              <input
                type="range"
                min="0"
                max="40"
                value={configFields.cta_border_radius || 8}
                onChange={(e) => update({ cta_border_radius: parseInt(e.target.value) })}
                className="w-full"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * ModuleConfigFields — Renders the form fields for a given module type.
 * Shared between Module Library (legacy defaults) and Module Instances.
 *
 * Props:
 *   moduleType: string  (one of: hero | text | header | navigation | footer | go_to | ...)
 *   configFields: object  (current values)
 *   setConfigFields: setter  (accepts new object or updater fn)
 *   uploading?: bool
 *   onImageUpload?: (event, fieldName) => Promise<void>
 *   allPages?: { modular: [], preset: [], custom: [] }
 */

// Live preview swatch for the go_to CTA config — renders a single mini
// CTA using the same style rules as `ModularPage`'s go_to renderer so
// admins can see the effect of style/colour/size/icon choices without
// hitting Save. Kept lightweight (no navigation, no shadow scale) so it
// fits comfortably next to the config form.
function GoToCtaPreview({ config = {} }) {
  const style = config.cta_style || "card";
  const size = config.cta_size || "md";
  const btnPaddingMap = { xs: "px-4 py-2", sm: "px-5 py-2.5", md: "px-8 py-4", lg: "px-10 py-5", xl: "px-12 py-6" };
  const btnTextMap = { xs: "text-sm", sm: "text-base", md: "text-lg", lg: "text-xl", xl: "text-2xl" };
  const cardPaddingMap = { xs: "p-3", sm: "p-4", md: "p-6", lg: "p-8", xl: "p-10" };
  const cardTitleMap = { xs: "text-base", sm: "text-lg", md: "text-xl", lg: "text-2xl", xl: "text-3xl" };
  const btnPadding = btnPaddingMap[size] || "px-8 py-4";
  const btnText = btnTextMap[size] || "text-lg";
  const cardPadding = cardPaddingMap[size] || "p-6";
  const cardTitle = cardTitleMap[size] || "text-xl";

  const bgColor = config.cta_bg_color || "#2f4f4f";
  const textColor = config.cta_text_color || (style === "button-outline" ? bgColor : "#ffffff");
  const borderRadius = config.cta_border_radius != null && config.cta_border_radius !== "" ? config.cta_border_radius : 999;
  const borderWidth = style === "button-outline" ? (config.cta_border_width || 2) : (config.cta_border_width || 0);
  const borderColor = config.cta_border_color || bgColor;

  const title = config.cta1_title || "CTA preview";
  const description = config.cta1_description || "";
  const LeadingIcon = getCtaIcon(config.cta1_icon);

  if (style === "card") {
    return (
      <div className={`inline-block ${cardPadding} bg-white rounded-lg shadow-md border-2 border-transparent max-w-sm`}>
        <div className="flex items-start gap-3">
          {LeadingIcon && <LeadingIcon className="w-6 h-6 text-primary mt-1 shrink-0" />}
          <div>
            <div className={`${cardTitle} font-bold text-gray-900`}>{title}</div>
            {description && <div className="text-sm text-gray-600 mt-1">{description}</div>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <span
      className={`inline-flex items-center gap-2 font-semibold ${btnPadding} ${btnText} shadow whitespace-nowrap`}
      style={{
        backgroundColor: style === "button-outline" ? "transparent" : bgColor,
        color: textColor,
        borderRadius: `${borderRadius}px`,
        border: `${borderWidth}px solid ${borderColor}`,
      }}
    >
      {LeadingIcon && <LeadingIcon className="w-4 h-4" />}
      {title}
    </span>
  );
}

export default function ModuleConfigFields({
  moduleType,
  configFields,
  setConfigFields,
  uploading = false,
  onImageUpload = null,
  allPages = { modular: [], preset: [], custom: [] },
  instanceId = null,
}) {
  const update = (patch) => setConfigFields((prev) => ({ ...prev, ...patch }));

  // Fetch properties for the property_gallery selector. Loaded lazily but
  // always at hook-top so the rules of hooks are honoured regardless of
  // the conditional render branches below.
  const [properties, setProperties] = useState([]);
  useEffect(() => {
    if (moduleType !== "property_gallery") return;
    let cancelled = false;
    axios
      .get(`${API}/admin/properties`, { withCredentials: true })
      .then((r) => {
        if (cancelled) return;
        setProperties(r.data?.properties || r.data || []);
      })
      .catch(() => {
        // Fallback to the public endpoint if admin one is not reachable.
        axios.get(`${API}/properties`).then((r) => {
          if (!cancelled) setProperties(r.data || []);
        }).catch(() => {});
      });
    return () => { cancelled = true; };
  }, [moduleType]);

  if (moduleType === "hero") {
    return (
      <>
        <div className="grid grid-cols-[1fr_auto] gap-2 items-end">
          <div className="space-y-2">
            <Label>Title</Label>
            <Input
              data-testid="hero-title-input"
              placeholder="Welcome to our site"
              value={configFields.title || ""}
              onChange={(e) => update({ title: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Color</Label>
            <ColorPickerInput compact value={configFields.title_color || ""}
              onChange={(v) => update({ title_color: v })} />
          </div>
        </div>
        <div className="grid grid-cols-[1fr_auto] gap-2 items-end">
          <div className="space-y-2">
            <Label>Title — 2nd colour (optional)</Label>
            <Input
              data-testid="hero-title-part2-input"
              placeholder='e.g. "without an estate agent"'
              value={configFields.title_part2 || ""}
              onChange={(e) => update({ title_part2: e.target.value })}
            />
            <p className="text-xs text-muted-foreground">
              Appends after Title in a second colour — e.g. "Sell your property" + "without an estate agent"
            </p>
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Color</Label>
            <ColorPickerInput compact value={configFields.title_part2_color || ""}
              onChange={(v) => update({ title_part2_color: v })} />
          </div>
        </div>
        <TextStyleControls
          label="Title Style"
          prefix="title_"
          config={configFields}
          update={update}
          preview={configFields.title || "Welcome to our site"}
          compact
          hideAlign
        />
        <AlignmentToggle
          label="Title Alignment"
          value={configFields.title_text_align}
          onChange={(v) => update({ title_text_align: v })}
          testId="hero-title-align"
        />
        <div className="grid grid-cols-[1fr_auto] gap-2 items-end">
          <div className="space-y-2">
            <Label>Subtitle</Label>
            <Input
              placeholder="Discover amazing content"
              value={configFields.subtitle || ""}
              onChange={(e) => update({ subtitle: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Color</Label>
            <ColorPickerInput compact value={configFields.subtitle_color || ""}
              onChange={(v) => update({ subtitle_color: v })} />
          </div>
        </div>
        <TextStyleControls
          label="Subtitle Style"
          prefix="subtitle_"
          config={configFields}
          update={update}
          preview={configFields.subtitle || "Discover amazing content"}
          compact
          hideAlign
        />
        <AlignmentToggle
          label="Subtitle Alignment"
          value={configFields.subtitle_text_align}
          onChange={(v) => update({ subtitle_text_align: v })}
          testId="hero-subtitle-align"
        />
        <div className="space-y-2">
          <Label>Background Image</Label>
          {configFields.background_image && (
            <div className="relative w-full h-32 mb-2 border rounded overflow-hidden">
              <img src={configFields.background_image} alt="Background preview"
                className="w-full h-full object-cover"
                style={{
                  transform: `scale(${(configFields.image_zoom || 100) / 100}) rotate(${configFields.image_rotate || 0}deg)`,
                  opacity: (configFields.image_opacity || 100) / 100,
                  filter: `contrast(${configFields.image_contrast || 100}%) brightness(${configFields.image_brightness || 100}%)`,
                }} />
              <Button variant="destructive" size="sm" className="absolute top-2 right-2"
                onClick={() => update({ background_image: "" })}>Remove</Button>
            </div>
          )}
          {onImageUpload && (
            <Input type="file" accept="image/*"
              onChange={(e) => onImageUpload(e, "background_image")} disabled={uploading} />
          )}
          {uploading && <p className="text-sm text-muted-foreground">Uploading...</p>}
        </div>

        {configFields.background_image && (
          <div className="space-y-4 pt-4 border-t">
            <h4 className="font-medium text-sm">Image Adjustments</h4>
            {[
              { key: "image_zoom", label: "Zoom", min: 50, max: 200, unit: "%" },
              { key: "image_rotate", label: "Rotate", min: -180, max: 180, unit: "°" },
              { key: "image_opacity", label: "Opacity", min: 0, max: 100, unit: "%" },
              { key: "image_contrast", label: "Contrast", min: 0, max: 200, unit: "%" },
              { key: "image_brightness", label: "Brightness", min: 0, max: 200, unit: "%" },
            ].map(({ key, label, min, max, unit }) => (
              <div key={key} className="space-y-2">
                <Label>{label}: {configFields[key] ?? (key === "image_rotate" ? 0 : 100)}{unit}</Label>
                <input type="range" min={min} max={max}
                  value={configFields[key] ?? (key === "image_rotate" ? 0 : 100)}
                  onChange={(e) => update({ [key]: parseInt(e.target.value) })}
                  className="w-full" />
              </div>
            ))}
          </div>
        )}

        <div className="space-y-2">
          <Label>CTA Button Text</Label>
          <Input placeholder="Get Started" value={configFields.cta_text || ""}
            onChange={(e) => update({ cta_text: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label>CTA Button Link</Label>
          <Input placeholder="/contact" value={configFields.cta_link || ""}
            onChange={(e) => update({ cta_link: e.target.value })} />
        </div>
        <AlignmentToggle
          label="CTA Button Alignment"
          value={configFields.cta_align}
          onChange={(v) => update({ cta_align: v })}
          testId="hero-cta-align"
        />

        <div className="space-y-3 pt-3 border-t">
          <h4 className="font-medium text-sm">CTA Button Font Style</h4>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Font Family</Label>
              <select value={configFields.cta_font_family || "inherit"}
                onChange={(e) => update({ cta_font_family: e.target.value })}
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm">
                <option value="inherit">Default (Site Body)</option>
                <option value="'Playfair Display', serif">Playfair Display (serif)</option>
                <option value="'Inter', sans-serif">Inter (sans-serif)</option>
                <option value="'Montserrat', sans-serif">Montserrat</option>
                <option value="'Poppins', sans-serif">Poppins</option>
                <option value="'Lora', serif">Lora (serif)</option>
                <option value="'Roboto', sans-serif">Roboto</option>
                <option value="'Open Sans', sans-serif">Open Sans</option>
                <option value="'Merriweather', serif">Merriweather</option>
                <option value="'Source Sans Pro', sans-serif">Source Sans Pro</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>Font Weight</Label>
              <select value={configFields.cta_font_weight || "500"}
                onChange={(e) => update({ cta_font_weight: e.target.value })}
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm">
                <option value="300">Light (300)</option>
                <option value="400">Regular (400)</option>
                <option value="500">Medium (500)</option>
                <option value="600">Semi Bold (600)</option>
                <option value="700">Bold (700)</option>
                <option value="800">Extra Bold (800)</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>Font Style</Label>
              <select value={configFields.cta_font_style || "normal"}
                onChange={(e) => update({ cta_font_style: e.target.value })}
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm">
                <option value="normal">Normal</option>
                <option value="italic">Italic</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>Text Transform</Label>
              <select value={configFields.cta_text_transform || "none"}
                onChange={(e) => update({ cta_text_transform: e.target.value })}
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm">
                <option value="none">None</option>
                <option value="uppercase">UPPERCASE</option>
                <option value="lowercase">lowercase</option>
                <option value="capitalize">Capitalize</option>
              </select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Heading size (H1 – H6)</Label>
            <HeadingLevelSelect
              value={configFields.cta_font_size}
              onChange={(lvl) => update({ cta_font_size: lvl })}
              testId="hero-cta-heading-level"
            />
          </div>
          <div className="space-y-2">
            <Label>Letter Spacing: {configFields.cta_letter_spacing || 0}px</Label>
            <input type="range" min="-2" max="10" step="0.5" value={configFields.cta_letter_spacing || 0}
              onChange={(e) => update({ cta_letter_spacing: parseFloat(e.target.value) })} className="w-full" />
          </div>
          {/* CTA Button Size — preset + optional custom padding. Presets map
              1:1 to the paddings used by <HeroCtaLink> so preview and live
              render stay in sync. */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Button Size</Label>
              <select
                value={configFields.cta_size || "lg"}
                onChange={(e) => update({ cta_size: e.target.value, cta_padding_x: "", cta_padding_y: "" })}
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                data-testid="hero-cta-size"
              >
                <option value="sm">Small (16 × 8)</option>
                <option value="md">Medium (24 × 12)</option>
                <option value="lg">Large (32 × 16) — default</option>
                <option value="xl">Extra Large (48 × 20)</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Or override</Label>
              <div className="flex items-center gap-2">
                <input
                  type="number" min="0" max="120" step="2"
                  placeholder="px X"
                  value={configFields.cta_padding_x ?? ""}
                  onChange={(e) => update({ cta_padding_x: e.target.value })}
                  className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
                  data-testid="hero-cta-padding-x"
                />
                <input
                  type="number" min="0" max="60" step="2"
                  placeholder="px Y"
                  value={configFields.cta_padding_y ?? ""}
                  onChange={(e) => update({ cta_padding_y: e.target.value })}
                  className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
                  data-testid="hero-cta-padding-y"
                />
              </div>
            </div>
          </div>
          {/* CTA Icon — optional glyph next to the label. Uses the shared
              CTA_ICONS catalog so Hero and go_to draw from the same set. */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Icon (optional)</Label>
              <div className="flex items-center gap-2">
                <select
                  value={configFields.cta_icon || ""}
                  onChange={(e) => update({ cta_icon: e.target.value })}
                  className="flex-1 h-9 rounded-md border border-input bg-background px-2 text-sm"
                  data-testid="hero-cta-icon"
                >
                  <option value="">— No icon —</option>
                  {Object.entries(CTA_ICONS).map(([key, meta]) => (
                    <option key={key} value={key}>{meta.label}</option>
                  ))}
                </select>
                {(() => {
                  const Ic = getCtaIcon(configFields.cta_icon);
                  return Ic ? (
                    <span className="h-9 w-9 border border-input rounded-md inline-flex items-center justify-center text-primary bg-muted/40">
                      <Ic className="w-4 h-4" />
                    </span>
                  ) : null;
                })()}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Icon position</Label>
              <div className="flex items-center gap-2">
                <select
                  value={configFields.cta_icon_position || "left"}
                  onChange={(e) => update({ cta_icon_position: e.target.value })}
                  disabled={!configFields.cta_icon}
                  className="flex-1 h-9 rounded-md border border-input bg-background px-2 text-sm disabled:opacity-50"
                  data-testid="hero-cta-icon-position"
                >
                  <option value="left">Left of text</option>
                  <option value="right">Right of text</option>
                </select>
                <input
                  type="number" min="10" max="48" step="1"
                  placeholder="px"
                  value={configFields.cta_icon_size ?? ""}
                  onChange={(e) => update({ cta_icon_size: e.target.value })}
                  disabled={!configFields.cta_icon}
                  className="w-16 h-9 rounded-md border border-input bg-background px-2 text-sm disabled:opacity-50"
                  data-testid="hero-cta-icon-size"
                  title="Icon size in px (default 20)"
                />
              </div>
            </div>
          </div>
          <div className="p-4 bg-gradient-to-br from-blue-600 to-purple-600 rounded text-center">
            {(() => {
              const Ic = getCtaIcon(configFields.cta_icon);
              const iconSize = Number(configFields.cta_icon_size) || 20;
              const pos = configFields.cta_icon_position === "right" ? "row-reverse" : "row";
              return (
                <span
                  className="inline-flex items-center"
                  style={{
                    padding: `${configFields.cta_padding_y || ({ sm: 8, md: 12, lg: 16, xl: 20 }[configFields.cta_size || "lg"])}px ${configFields.cta_padding_x || ({ sm: 16, md: 24, lg: 32, xl: 48 }[configFields.cta_size || "lg"])}px`,
                    gap: Ic ? "8px" : 0,
                    flexDirection: pos,
                    fontFamily: configFields.cta_font_family || "inherit",
                    fontSize: resolveFontSize(configFields.cta_font_size),
                    fontWeight: configFields.cta_font_weight || 500,
                    fontStyle: configFields.cta_font_style || "normal",
                    textTransform: configFields.cta_text_transform || "none",
                    letterSpacing: `${configFields.cta_letter_spacing || 0}px`,
                    backgroundColor: configFields.cta_bg_color || "#ffffff",
                    color: configFields.cta_text_color || "#2f4f4f",
                    border: `${configFields.cta_border_width || 0}px solid ${configFields.cta_border_color || "transparent"}`,
                    borderRadius: `${configFields.cta_border_radius || 8}px`,
                  }}>
                  {Ic && <Ic style={{ width: iconSize, height: iconSize, flexShrink: 0 }} />}
                  <span>{configFields.cta_text || "Preview Button"}</span>
                </span>
              );
            })()}
          </div>
        </div>

        <div className="space-y-3 pt-3 border-t">
          <h4 className="font-medium text-sm">CTA Button Colors</h4>
          <div className="grid grid-cols-2 gap-3">
            <ColorPickerInput label="Background" compact value={configFields.cta_bg_color || "#ffffff"}
              onChange={(v) => update({ cta_bg_color: v })} />
            <ColorPickerInput label="Text" compact value={configFields.cta_text_color || "#2f4f4f"}
              onChange={(v) => update({ cta_text_color: v })} />
            <ColorPickerInput label="Hover Background" compact value={configFields.cta_hover_bg_color || "#f3f4f6"}
              onChange={(v) => update({ cta_hover_bg_color: v })} />
            <ColorPickerInput label="Hover Text" compact value={configFields.cta_hover_text_color || "#2f4f4f"}
              onChange={(v) => update({ cta_hover_text_color: v })} />
            <ColorPickerInput label="Border Color" compact
              value={configFields.cta_border_color === "transparent" ? "#000000" : (configFields.cta_border_color || "#000000")}
              onChange={(v) => update({ cta_border_color: v })} />
            <div className="space-y-2">
              <Label>Border Width: {configFields.cta_border_width || 0}px</Label>
              <input type="range" min="0" max="6" step="0.5" value={configFields.cta_border_width || 0}
                onChange={(e) => update({ cta_border_width: parseFloat(e.target.value) })} className="w-full" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Corner Radius: {configFields.cta_border_radius || 8}px</Label>
            <input type="range" min="0" max="40" step="1" value={configFields.cta_border_radius || 8}
              onChange={(e) => update({ cta_border_radius: parseInt(e.target.value) })} className="w-full" />
          </div>
        </div>

        <div className="space-y-2 pt-2 border-t">
          <Label>Banner Height: {configFields.banner_height || 500}px</Label>
          <input type="range" min="200" max="900" step="50" value={configFields.banner_height || 500}
            onChange={(e) => update({ banner_height: parseInt(e.target.value) })} className="w-full" />
          <p className="text-xs text-muted-foreground">Controls the minimum height of the hero banner (200–900px)</p>
        </div>
      </>
    );
  }

  if (moduleType === "text") {
    return (
      <TextModuleConfig
        configFields={configFields}
        update={update}
        allPages={allPages}
      />
    );
  }

  if (moduleType === "header") {
    return (
      <>
        <div className="space-y-2">
          <Label>Logo Image</Label>
          {configFields.logo_url && (
            <div className="relative w-32 h-16 mb-2 border rounded overflow-hidden">
              <img src={configFields.logo_url} alt="Logo preview" className="w-full h-full object-contain" />
              <Button variant="destructive" size="sm" className="absolute top-1 right-1"
                onClick={() => update({ logo_url: "" })}>
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          )}
          {onImageUpload && (
            <Input type="file" accept="image/*"
              onChange={(e) => onImageUpload(e, "logo_url")} disabled={uploading} />
          )}
          {uploading && <p className="text-sm text-muted-foreground">Uploading...</p>}
          <p className="text-[11px] text-muted-foreground">
            Upload your own logo, or leave blank to inherit the Company Logo from Admin → Site Styles.
          </p>
        </div>
        <LogoImageStyleControls configFields={configFields} update={update} />
        <div className="space-y-2">
          <Label>Logo Text</Label>
          <Input placeholder="Sell Local Homes" value={configFields.logo_text || ""}
            onChange={(e) => update({ logo_text: e.target.value })} />
        </div>
        <TextStyleControls
          label="Logo Text Style"
          prefix="logo_"
          config={configFields}
          update={update}
          preview={configFields.logo_text || "Sell Local Homes"}
          compact
        />
        <div className="grid grid-cols-[1fr_auto] gap-3 pt-2 border-t items-end">
          <div className="space-y-2">
            <Label>Strap Line</Label>
            <Input placeholder="Your trusted partner in selling your home"
              value={configFields.strap_line || ""}
              onChange={(e) => update({ strap_line: e.target.value })}
              data-testid="header-strapline-input" />
          </div>
          <ColorPickerInput label="Color" compact
            value={configFields.strap_line_color || ""}
            onChange={(v) => update({ strap_line_color: v })} />
        </div>
        <TextStyleControls
          label="Strap Line Style"
          prefix="strap_line_"
          config={configFields}
          update={update}
          preview={configFields.strap_line || "Your trusted partner in selling your home"}
          compact
        />
      </>
    );
  }

  if (moduleType === "navigation") {
    return (
      <>
        <p className="text-xs text-muted-foreground bg-blue-50 border border-blue-200 rounded p-2">
          Nav links are managed via the <strong>Navigation</strong> CMS tab. Logo image and text normally live on the <strong>Header</strong> module — turn on "Show logo on this bar" below only if you want this nav row to carry its own logo instead.
        </p>

        {/* Logo block — off by default; shows the company logo stacked above
            the company name on the far left, with the nav links beside it on
            the same row, instead of today's separate strap-line row. */}
        <div className="pt-2 border-t space-y-3">
          <div className="flex items-center gap-3">
            <input type="checkbox" id="nav_show_logo" checked={configFields.show_logo === true}
              onChange={(e) => update({ show_logo: e.target.checked })} data-testid="nav-show-logo-toggle" />
            <Label htmlFor="nav_show_logo">Show logo + company name on this bar</Label>
          </div>
          {configFields.show_logo === true && (
            <div className="pl-6 space-y-3">
              <div className="space-y-2">
                <Label>Logo Image</Label>
                {configFields.logo_url && (
                  <div className="relative w-28 h-14 border rounded overflow-hidden">
                    <img src={configFields.logo_url} alt="Logo preview" className="w-full h-full object-contain" />
                    <Button variant="destructive" size="sm" className="absolute top-1 right-1"
                      onClick={() => update({ logo_url: "" })}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                )}
                {onImageUpload && (
                  <Input type="file" accept="image/*"
                    onChange={(e) => onImageUpload(e, "logo_url")} disabled={uploading} data-testid="nav-logo-upload-input" />
                )}
                <p className="text-[11px] text-muted-foreground">
                  Leave blank to inherit the Company Logo from Admin → Site Styles.
                </p>
              </div>
              <LogoImageStyleControls configFields={configFields} update={update} />
              <div className="space-y-2">
                <Label>Company Name</Label>
                <Input placeholder="Sell Local Homes" value={configFields.logo_text || ""}
                  onChange={(e) => update({ logo_text: e.target.value })} data-testid="nav-logo-text-input" />
              </div>
              <TextStyleControls
                label="Company Name Style"
                prefix="logo_"
                config={configFields}
                update={update}
                preview={configFields.logo_text || "Sell Local Homes"}
                compact
              />
            </div>
          )}
        </div>

        {/* Icon / label display mode ------------------------------------ */}
        <div className="space-y-2">
          <Label>Link display</Label>
          <div className="inline-flex border border-border rounded overflow-hidden text-sm" role="radiogroup" data-testid="nav-display-mode">
            {[
              { v: "both", label: "Icon + text" },
              { v: "text", label: "Text only" },
              { v: "icon", label: "Icon only" },
            ].map((opt) => {
              const active = (configFields.display_mode || "both") === opt.v;
              return (
                <button
                  key={opt.v}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => update({ display_mode: opt.v })}
                  className={`px-3 py-1.5 font-medium transition-colors ${active ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}
                  data-testid={`nav-display-mode-${opt.v}`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
          <p className="text-xs text-muted-foreground">
            Icon-only links keep their label as an accessible <code>aria-label</code> + tooltip for screen readers.
          </p>
        </div>

        <div className="grid grid-cols-[1fr_auto] gap-3 items-end">
          <div className="space-y-2">
            <Label>Strap Line</Label>
            <Input
              placeholder="Your trusted partner in selling your home"
              value={configFields.strap_line || ""}
              onChange={(e) => update({ strap_line: e.target.value })}
              data-testid="navigation-strapline-input"
            />
          </div>
          <ColorPickerInput label="Color" compact
            value={configFields.strap_line_color || ""}
            onChange={(v) => update({ strap_line_color: v })} />
        </div>
        <TextStyleControls
          label="Strap Line Style"
          prefix="strap_line_"
          config={configFields}
          update={update}
          preview={configFields.strap_line || "Your trusted partner in selling your home"}
          compact
        />
        <TextStyleControls
          label="Link Text Style"
          prefix="nav_link_"
          config={configFields}
          update={update}
          preview={configFields.strap_line || "Home · About · Contact"}
          compact
        />
        <div className="space-y-1 -mt-2">
          <Label className="text-xs">Link font size (px)</Label>
          <Input
            type="number"
            min="8"
            max="48"
            step="1"
            placeholder="Auto (uses heading size)"
            value={
              typeof configFields.nav_link_font_size === "number"
                ? configFields.nav_link_font_size
                : (typeof configFields.nav_link_font_size === "string" && /^\d+$/.test(configFields.nav_link_font_size))
                    ? Number(configFields.nav_link_font_size)
                    : ""
            }
            onChange={(e) => {
              const raw = e.target.value.trim();
              // Clearing the input falls back to the H-level selector above.
              if (raw === "") { update({ nav_link_font_size: "" }); return; }
              const n = Math.max(8, Math.min(48, parseInt(raw, 10) || 14));
              update({ nav_link_font_size: n });
            }}
            data-testid="navigation-link-font-size-px"
          />
          <p className="text-[10px] text-muted-foreground">
            Numeric px overrides the H1-H6 heading size chosen above. Leave blank to fall back.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3 pt-2 border-t">
          <ColorPickerInput label="Background Color" compact value={configFields.bg_color || ""}
            onChange={(v) => update({ bg_color: v })} />
          <ColorPickerInput label="Link Text Color" compact value={configFields.text_color || ""}
            onChange={(v) => update({ text_color: v })} />
          <ColorPickerInput label="Link Hover Color" compact value={configFields.hover_color || ""}
            onChange={(v) => update({ hover_color: v })} />
          <div className="space-y-2">
            <Label>Heading size (H1 – H6)</Label>
            <HeadingLevelSelect
              value={configFields.font_size}
              onChange={(lvl) => update({ font_size: lvl })}
              testId="navigation-heading-level"
            />
          </div>
        </div>
        <div className="pt-2 border-t space-y-3">
          <div className="flex items-center gap-3">
            <input type="checkbox" id="cta_enabled" checked={configFields.cta_enabled !== false}
              onChange={(e) => update({ cta_enabled: e.target.checked })} />
            <Label htmlFor="cta_enabled">Show CTA Button</Label>
          </div>
          {configFields.cta_enabled !== false && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs">CTA Text</Label>
                  <Input placeholder="Request Viewing" value={configFields.cta_text || ""}
                    onChange={(e) => update({ cta_text: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">CTA Link</Label>
                  <Input placeholder="/contact" value={configFields.cta_link || ""}
                    onChange={(e) => update({ cta_link: e.target.value })} />
                </div>
                <ColorPickerInput label="CTA Background" compact value={configFields.cta_bg_color || ""}
                  onChange={(v) => update({ cta_bg_color: v })} />
                <ColorPickerInput label="CTA Text Color" compact value={configFields.cta_text_color || ""}
                  onChange={(v) => update({ cta_text_color: v })} />
              </div>
              <TextStyleControls
                label="CTA Button Text Style"
                prefix="cta_"
                config={configFields}
                update={update}
                preview={configFields.cta_text || "Request Viewing"}
                compact
              />
            </>
          )}
        </div>
      </>
    );
  }

  if (moduleType === "footer") {
    return (
      <>
        <div className="space-y-2">
          <Label>Company Logo</Label>
          {configFields.logo_url && (
            <div className="relative w-32 h-16 mb-2 border rounded overflow-hidden bg-slate-100">
              <img src={configFields.logo_url} alt="Footer logo preview" className="w-full h-full object-contain" />
              <Button
                variant="destructive"
                size="sm"
                className="absolute top-1 right-1"
                onClick={() => update({ logo_url: "" })}
                data-testid="footer-logo-clear"
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          )}
          {onImageUpload && (
            <Input
              type="file"
              accept="image/*"
              onChange={(e) => onImageUpload(e, "logo_url")}
              disabled={uploading}
              data-testid="footer-logo-upload"
            />
          )}
          {uploading && <p className="text-sm text-muted-foreground">Uploading…</p>}
          <p className="text-[11px] text-muted-foreground">
            Upload your own logo, or leave blank to inherit the Company Logo from Admin → Site Styles.
          </p>
        </div>
        <div className="space-y-2">
          <Label>Company Name</Label>
          <Input placeholder="SellLocalHomes" value={configFields.company_name || ""}
            onChange={(e) => update({ company_name: e.target.value })}
            data-testid="footer-company-name" />
        </div>
        <TextStyleControls
          label="Company Name Style"
          prefix="company_name_"
          config={configFields}
          update={update}
          preview={configFields.company_name || "SellLocalHomes"}
          compact
        />
        <div className="space-y-2">
          <Label>Tagline</Label>
          <Input placeholder="Your trusted partner" value={configFields.tagline || ""}
            onChange={(e) => update({ tagline: e.target.value })} />
        </div>
        <TextStyleControls
          label="Tagline Style"
          prefix="tagline_"
          config={configFields}
          update={update}
          preview={configFields.tagline || "Your trusted partner"}
          compact
        />
        <div className="space-y-2">
          <Label>Copyright Year</Label>
          <Input type="number" placeholder="2025" value={configFields.copyright_year || ""}
            onChange={(e) => update({ copyright_year: parseInt(e.target.value) })} />
        </div>

        {/* Partner / accreditation logos ------------------------------- */}
        <div className="border rounded-lg p-3 space-y-3 bg-slate-50/50">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-sm">Partner / Accreditation Logos</h3>
              <p className="text-xs text-muted-foreground">
                Each logo can carry a URL — clicks open in a new tab.
              </p>
            </div>
            <button
              type="button"
              className="text-xs px-2 py-1 bg-primary text-primary-foreground rounded hover:opacity-90"
              onClick={() => {
                const next = [...(configFields.partner_logos || []), { image_url: "", link_url: "", alt: "" }];
                update({ partner_logos: next });
              }}
              data-testid="footer-add-partner-logo"
            >
              + Add logo
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Logo height (px)</Label>
              <Input
                type="number"
                min={16}
                max={200}
                placeholder="40"
                value={configFields.partner_logos_size_px || 40}
                onChange={(e) => update({ partner_logos_size_px: parseInt(e.target.value || "40", 10) })}
                className="h-8"
                data-testid="footer-partner-logos-size"
              />
              <p className="text-[10px] text-muted-foreground">All logos render at this uniform height. Widths auto-scale.</p>
            </div>
          </div>

          {(configFields.partner_logos || []).length === 0 && (
            <p className="text-xs text-muted-foreground italic text-center py-2">
              No partner logos yet. Click <strong>+ Add logo</strong> to add one.
            </p>
          )}

          {(configFields.partner_logos || []).map((logo, idx) => {
            const patchLogo = (patch) => {
              const next = [...(configFields.partner_logos || [])];
              next[idx] = { ...next[idx], ...patch };
              update({ partner_logos: next });
            };
            const removeLogo = () => {
              const next = (configFields.partner_logos || []).filter((_, i) => i !== idx);
              update({ partner_logos: next });
            };
            const moveLogo = (delta) => {
              const arr = [...(configFields.partner_logos || [])];
              const target = idx + delta;
              if (target < 0 || target >= arr.length) return;
              const [x] = arr.splice(idx, 1);
              arr.splice(target, 0, x);
              update({ partner_logos: arr });
            };
            return (
              <div
                key={idx}
                className="border rounded p-3 space-y-2 bg-white"
                data-testid={`footer-partner-logo-row-${idx}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-medium text-muted-foreground">Logo {idx + 1}</span>
                  <div className="flex items-center gap-1">
                    <button type="button" className="text-xs px-1.5 py-0.5 border rounded hover:bg-muted" onClick={() => moveLogo(-1)} data-testid={`footer-partner-logo-up-${idx}`}>▲</button>
                    <button type="button" className="text-xs px-1.5 py-0.5 border rounded hover:bg-muted" onClick={() => moveLogo(1)}  data-testid={`footer-partner-logo-down-${idx}`}>▼</button>
                    <button type="button" className="text-xs px-1.5 py-0.5 border border-red-300 text-red-600 rounded hover:bg-red-50" onClick={removeLogo} data-testid={`footer-partner-logo-remove-${idx}`}>×</button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 items-start">
                  <div className="space-y-1">
                    <Label className="text-[11px]">Image</Label>
                    {logo.image_url && (
                      <img src={logo.image_url} alt="preview" className="h-10 w-auto object-contain bg-slate-100 border rounded p-1" />
                    )}
                    <ImageUpload
                      onUpload={(url) => patchLogo({ image_url: url })}
                      multiple={false}
                      label={logo.image_url ? "Replace image" : "Upload image"}
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="space-y-1">
                      <Label className="text-[11px]">Link URL (opens in new tab)</Label>
                      <Input
                        placeholder="https://example.com"
                        value={logo.link_url || ""}
                        onChange={(e) => patchLogo({ link_url: e.target.value })}
                        className="h-8 text-xs"
                        data-testid={`footer-partner-logo-url-${idx}`}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[11px]">Alt text (accessibility)</Label>
                      <Input
                        placeholder="e.g. Ombudsman member"
                        value={logo.alt || ""}
                        onChange={(e) => patchLogo({ alt: e.target.value })}
                        className="h-8 text-xs"
                        data-testid={`footer-partner-logo-alt-${idx}`}
                      />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </>
    );
  }

  if (moduleType === "go_to") {
    return (
      <>
        <div className="p-3 bg-slate-50 rounded-lg border space-y-3">
          <h3 className="font-semibold text-sm">Layout & Style</h3>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Heading size</Label>
              <HeadingLevelSelect
                value={configFields.font_size}
                onChange={(lvl) => update({ font_size: lvl })}
                testId="goto-heading-level"
                className="h-8"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">CTA Size</Label>
              <select value={configFields.cta_size || "md"}
                onChange={(e) => update({ cta_size: e.target.value })}
                className="w-full h-8 px-2 border border-input rounded-md bg-background text-sm"
                data-testid="goto-cta-size">
                <option value="xs">Extra small</option>
                <option value="sm">Small</option>
                <option value="md">Medium</option>
                <option value="lg">Large</option>
                <option value="xl">Extra large</option>
              </select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Placement</Label>
              <select value={configFields.cta_placement || "horizontal"}
                onChange={(e) => update({ cta_placement: e.target.value })}
                className="w-full h-8 px-2 border border-input rounded-md bg-background text-sm"
                data-testid="goto-cta-placement">
                <option value="horizontal">2 Columns</option>
                <option value="grid-2">2×2 Grid</option>
                <option value="grid-4">4 Columns</option>
                <option value="vertical">Vertical Stack</option>
                <option value="spread">Spread (far edges)</option>
              </select>
            </div>
          </div>

          {/* CTA style + colour controls — makes go_to CTAs look consistent
              with the Hero CTA button on other pages. */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">CTA Style</Label>
              <select value={configFields.cta_style || "card"}
                onChange={(e) => update({ cta_style: e.target.value })}
                className="w-full h-8 px-2 border border-input rounded-md bg-background text-sm"
                data-testid="goto-cta-style">
                <option value="card">Card (title + description)</option>
                <option value="button-solid">Button — solid</option>
                <option value="button-outline">Button — outline</option>
              </select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Border radius (px)</Label>
              <Input
                type="number"
                min="0"
                max="999"
                value={configFields.cta_border_radius ?? ""}
                onChange={(e) => update({ cta_border_radius: e.target.value === "" ? "" : Number(e.target.value) })}
                placeholder="999 (pill)"
                className="h-8"
                data-testid="goto-cta-border-radius"
              />
            </div>
          </div>

          {(configFields.cta_style === "button-solid" || configFields.cta_style === "button-outline") && (
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Background</Label>
                <ColorPickerInput
                  value={configFields.cta_bg_color || ""}
                  onChange={(v) => update({ cta_bg_color: v })}
                  data-testid="goto-cta-bg-color"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Text colour</Label>
                <ColorPickerInput
                  value={configFields.cta_text_color || ""}
                  onChange={(v) => update({ cta_text_color: v })}
                  data-testid="goto-cta-text-color"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Border colour</Label>
                <ColorPickerInput
                  value={configFields.cta_border_color || ""}
                  onChange={(v) => update({ cta_border_color: v })}
                  data-testid="goto-cta-border-color"
                />
              </div>
            </div>
          )}

          {/* Live preview swatch — renders a mini CTA using the current
              config values so admins can see the style/colour/size choices
              take effect without leaving the editor. */}
          <div className="pt-2 border-t">
            <Label className="text-xs text-muted-foreground uppercase tracking-wide">Live preview</Label>
            <div className="mt-2 p-4 rounded bg-slate-50 flex items-center justify-center min-h-[80px]" data-testid="goto-cta-live-preview">
              <GoToCtaPreview config={configFields} />
            </div>
          </div>

          <p className="text-xs text-muted-foreground">CTAs without a title will be hidden on the live page.</p>
        </div>
        {[1, 2, 3, 4].map((n) => (
          <div key={n} className="border rounded-lg p-3 space-y-3">
            <h3 className="font-medium text-sm text-muted-foreground">CTA {n}</h3>
            <div className="space-y-2">
              <Label className="text-xs">Title <span className="text-muted-foreground">(leave blank to hide)</span></Label>
              <Input placeholder={n === 1 ? "View All Properties" : `CTA ${n} Title`}
                value={configFields[`cta${n}_title`] || ""}
                onChange={(e) => update({ [`cta${n}_title`]: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Description (Optional)</Label>
              <Input placeholder="Short description…"
                value={configFields[`cta${n}_description`] || ""}
                onChange={(e) => update({ [`cta${n}_description`]: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Navigate To</Label>
              <select value={configFields[`cta${n}_page_slug`] || ""}
                onChange={(e) => update({ [`cta${n}_page_slug`]: e.target.value })}
                className="w-full h-10 px-3 py-2 border border-input rounded-md bg-background text-sm">
                <option value="">Select a page</option>
                {allPages.modular?.length > 0 && (
                  <optgroup label="Modular Pages">
                    {allPages.modular.map((page) => (
                      <option key={page.composition_id} value={page.page_slug === null ? "/" : (page.page_slug || "/")}>
                        {page.page_title} ({page.page_slug === null ? "/" : `/${page.page_slug}`})
                      </option>
                    ))}
                  </optgroup>
                )}
                {allPages.preset?.length > 0 && (
                  <optgroup label="Preset Pages">
                    {allPages.preset.map((page) => (
                      <option key={page.nav_id} value={page.path}>
                        {page.title} ({page.path})
                      </option>
                    ))}
                  </optgroup>
                )}
                {allPages.custom?.length > 0 && (
                  <optgroup label="Custom Pages">
                    {allPages.custom.map((page) => (
                      <option key={page.page_id} value={`/${page.slug}`}>
                        {page.title} (/{page.slug})
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Icon (optional)</Label>
              <div className="flex items-center gap-2">
                <select
                  value={configFields[`cta${n}_icon`] || ""}
                  onChange={(e) => update({ [`cta${n}_icon`]: e.target.value })}
                  className="flex-1 h-9 px-2 border border-input rounded-md bg-background text-sm"
                  data-testid={`goto-cta${n}-icon`}
                >
                  <option value="">— No icon —</option>
                  {Object.entries(CTA_ICONS).map(([key, meta]) => (
                    <option key={key} value={key}>{meta.label}</option>
                  ))}
                </select>
                {(() => {
                  const Ic = getCtaIcon(configFields[`cta${n}_icon`]);
                  return Ic ? (
                    <span className="h-9 w-9 border border-input rounded-md inline-flex items-center justify-center text-primary bg-muted/40">
                      <Ic className="w-4 h-4" />
                    </span>
                  ) : null;
                })()}
              </div>
            </div>
          </div>
        ))}
        {/* Shared text style — applies to every CTA card on this instance */}
        <TextStyleControls
          label="Card Title Style (applies to every CTA card)"
          prefix="title_"
          config={configFields}
          update={update}
          preview={configFields.cta1_title || "View All Properties"}
          compact
        />
        <TextStyleControls
          label="Card Description Style (applies to every CTA card)"
          prefix="description_"
          config={configFields}
          update={update}
          preview={configFields.cta1_description || "Short description…"}
          compact
        />
      </>
    );
  }

  if (moduleType === "all_properties" || moduleType === "featured_properties") {
    return <HeadingCtaFields configFields={configFields} update={update} allPages={allPages} />;
  }

  if (moduleType === "property_gallery") {
    return (
      <>
        <p className="text-xs text-muted-foreground bg-blue-50 border border-blue-200 rounded p-2">
          Choose which property&apos;s image gallery to display, then tune the
          grid layout below. Photos themselves are managed in the property
          owner&apos;s admin dashboard.
        </p>
        <div className="space-y-2">
          <Label>Heading <span className="text-muted-foreground">(optional)</span></Label>
          <Input
            placeholder="Property Gallery"
            value={configFields.heading_text || ""}
            onChange={(e) => update({ heading_text: e.target.value })}
            data-testid="property-gallery-heading-input"
          />
        </div>
        <TextStyleControls
          label="Heading Style"
          prefix="heading_"
          config={configFields}
          update={update}
          preview={configFields.heading_text || "Property Gallery"}
          compact
        />
        <div className="space-y-2">
          <Label>Property *</Label>
          <select
            value={configFields.property_id || ""}
            onChange={(e) => update({ property_id: e.target.value })}
            className="w-full h-10 px-3 py-2 border border-input rounded-md bg-background text-sm"
            data-testid="property-gallery-property-select"
          >
            <option value="">— Select a property —</option>
            {properties.map((p) => (
              <option key={p.property_id} value={p.property_id}>
                {p.title || p.cms_House_Name_Number || p.property_id}
                {p.gallery?.length ? ` (${p.gallery.length} images)` : ""}
              </option>
            ))}
          </select>
          {!configFields.property_id && (
            <p className="text-xs text-amber-700">
              No property selected — the module will be hidden on the live page until one is chosen.
            </p>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2 border-t">
          <div className="space-y-1">
            <Label className="text-xs">Columns</Label>
            <select
              value={configFields.columns || 3}
              onChange={(e) => update({ columns: parseInt(e.target.value) })}
              className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
              data-testid="property-gallery-columns"
            >
              <option value={1}>1</option>
              <option value={2}>2</option>
              <option value={3}>3</option>
              <option value={4}>4</option>
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Aspect ratio</Label>
            <select
              value={configFields.aspect_ratio || "4:3"}
              onChange={(e) => update({ aspect_ratio: e.target.value })}
              className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
              data-testid="property-gallery-aspect"
            >
              <option value="1:1">Square (1:1)</option>
              <option value="4:3">Photo (4:3)</option>
              <option value="3:2">Camera (3:2)</option>
              <option value="16:9">Wide (16:9)</option>
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Gap</Label>
            <select
              value={configFields.gap || "md"}
              onChange={(e) => update({ gap: e.target.value })}
              className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
              data-testid="property-gallery-gap"
            >
              <option value="sm">Small</option>
              <option value="md">Medium</option>
              <option value="lg">Large</option>
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Max images</Label>
            <Input
              type="number"
              min="0"
              placeholder="All"
              value={configFields.max_images ?? ""}
              onChange={(e) => update({ max_images: e.target.value === "" ? "" : parseInt(e.target.value) })}
              className="h-9"
              data-testid="property-gallery-max-images"
            />
          </div>
        </div>
        <div className="flex items-center gap-6 flex-wrap pt-2">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={configFields.rounded !== false}
              onChange={(e) => update({ rounded: e.target.checked })}
              data-testid="property-gallery-rounded"
            />
            Rounded corners
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={configFields.lightbox !== false}
              onChange={(e) => update({ lightbox: e.target.checked })}
              data-testid="property-gallery-lightbox"
            />
            Click-to-enlarge (lightbox)
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={!!configFields.show_featured_label}
              onChange={(e) => update({ show_featured_label: e.target.checked })}
              data-testid="property-gallery-featured-label"
            />
            Show &quot;Featured&quot; label on the first image
          </label>
        </div>
      </>
    );
  }

  if (moduleType === "card") {
    const ds = configFields.data_source || "manual";
    return (
      <>
        <div className="space-y-2">
          <Label>Data source</Label>
          <select
            value={ds}
            onChange={(e) => update({ data_source: e.target.value })}
            className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
            data-testid="card-data-source"
          >
            <option value="manual">Manual (single card)</option>
            <option value="properties_from_url">Property list (filtered by URL query)</option>
          </select>
          <p className="text-xs text-muted-foreground">
            Manual: one card with editable fields. Property list: auto-renders one card per matching property using URL query params (e.g. <code>?location=broxbourne&type=detached</code>).
          </p>
        </div>

        {ds === "manual" && (
          <>
            <div className="space-y-2">
              <Label>Image URL</Label>
              <Input
                placeholder="https://… or /uploads/…"
                value={configFields.image || ""}
                onChange={(e) => update({ image: e.target.value })}
                data-testid="card-image-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Heading</Label>
              <Input
                value={configFields.heading || ""}
                onChange={(e) => update({ heading: e.target.value })}
                data-testid="card-heading-input"
              />
            </div>
            <TextStyleControls
              label="Heading Style"
              prefix="heading_"
              config={configFields}
              update={update}
              preview={configFields.heading || "Card Heading"}
              compact
            />
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                rows={3}
                value={configFields.description || ""}
                onChange={(e) => update({ description: e.target.value })}
                data-testid="card-description-input"
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label>Type</Label>
                <Input
                  placeholder="e.g. Detached"
                  value={configFields.type_text || ""}
                  onChange={(e) => update({ type_text: e.target.value })}
                  data-testid="card-type-input"
                />
              </div>
              <div className="space-y-1">
                <Label>Location</Label>
                <Input
                  placeholder="e.g. Broxbourne"
                  value={configFields.location_text || ""}
                  onChange={(e) => update({ location_text: e.target.value })}
                  data-testid="card-location-input"
                />
              </div>
              <div className="space-y-1">
                <Label>Price</Label>
                <Input
                  placeholder="e.g. £450,000"
                  value={configFields.price_text || ""}
                  onChange={(e) => update({ price_text: e.target.value })}
                  data-testid="card-price-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>CTA Text</Label>
                <Input
                  value={configFields.cta_text || ""}
                  onChange={(e) => update({ cta_text: e.target.value })}
                  data-testid="card-cta-text-input"
                />
              </div>
              <div className="space-y-1">
                <Label>CTA Link</Label>
                <Input
                  value={configFields.cta_link || ""}
                  onChange={(e) => update({ cta_link: e.target.value })}
                  data-testid="card-cta-link-input"
                />
              </div>
            </div>
          </>
        )}

        {ds === "properties_from_url" && (
          <div className="p-3 bg-slate-50 rounded-lg border space-y-3">
            <h4 className="font-semibold text-sm">URL filter keys</h4>
            <p className="text-xs text-muted-foreground">
              Pick which URL query parameters are honored when filtering the property list.
            </p>
            <div className="grid grid-cols-2 gap-2 text-sm">
              {[
                { key: "filter_location",      label: "location" },
                { key: "filter_property_type", label: "type" },
                { key: "filter_min_price",     label: "min_price" },
                { key: "filter_max_price",     label: "max_price" },
                { key: "filter_beds",          label: "beds" },
                { key: "filter_baths",         label: "baths" },
                { key: "filter_featured",      label: "featured" },
              ].map(({ key, label }) => (
                <label key={key} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={!!configFields[key]}
                    onChange={(e) => update({ [key]: e.target.checked })}
                    data-testid={`card-${key.replace(/_/g, "-")}`}
                  />
                  <code className="text-xs">?{label}=</code>
                </label>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3 pt-2 border-t">
              <div className="space-y-1">
                <Label>Max results</Label>
                <Input
                  type="number"
                  min="1"
                  value={configFields.max_results ?? 12}
                  onChange={(e) => update({ max_results: Number(e.target.value) || 12 })}
                  data-testid="card-max-results"
                />
              </div>
              <div className="space-y-1">
                <Label>Empty state message</Label>
                <Input
                  value={configFields.empty_message || ""}
                  onChange={(e) => update({ empty_message: e.target.value })}
                  data-testid="card-empty-message"
                />
              </div>
            </div>
            <div className="space-y-1 pt-2 border-t">
              <Label>List heading (optional)</Label>
              <Input
                placeholder="e.g. Matching properties"
                value={configFields.heading || ""}
                onChange={(e) => update({ heading: e.target.value })}
                data-testid="card-list-heading"
              />
            </div>

            <details className="pt-2 border-t group" data-testid="card-url-help">
              <summary className="cursor-pointer text-sm font-medium select-none flex items-center gap-2 hover:text-primary">
                <span className="transition-transform group-open:rotate-90">▶</span>
                Example URL queries
              </summary>
              <div className="mt-3 space-y-3 text-xs">
                <p className="text-muted-foreground">
                  Append these query strings to any page that hosts this Card instance (e.g. <code>/properties</code>). The Card auto-renders one card per matching property using the toggled keys above.
                </p>
                <ul className="space-y-2">
                  {[
                    { q: "?location=broxbourne", desc: "Properties in Broxbourne (partial match, case-insensitive)" },
                    { q: "?type=detached", desc: "Detached homes only" },
                    { q: "?location=broxbourne&type=detached", desc: "Detached homes in Broxbourne" },
                    { q: "?min_price=400000&max_price=700000", desc: "Properties priced £400k–£700k" },
                    { q: "?beds=4", desc: "At least 4 bedrooms" },
                    { q: "?location=hertford&type=semi-detached&min_price=300000&beds=3", desc: "3+ bed semi-detached in Hertford, £300k minimum" },
                    { q: "?featured=true", desc: "Featured properties only (requires the featured toggle above)" },
                  ].map(({ q, desc }) => (
                    <li key={q} className="flex items-start gap-2">
                      <code className="bg-muted px-1.5 py-0.5 rounded text-[11px] font-mono whitespace-nowrap">{q}</code>
                      <span className="text-muted-foreground">{desc}</span>
                    </li>
                  ))}
                </ul>
                <div className="bg-blue-50 border border-blue-200 rounded p-2 text-blue-900">
                  <p className="font-semibold mb-1">Tips</p>
                  <ul className="list-disc list-inside space-y-0.5">
                    <li>Multiple filters combine with AND (e.g. <code>location</code> AND <code>type</code>).</li>
                    <li>Prices are integers (no commas, no currency symbol).</li>
                    <li><code>type</code> accepts <code>detached</code>, <code>semi-detached</code>, <code>terraced</code>, <code>bungalow</code>, <code>land</code> — dashes/spaces interchangeable.</li>
                    <li>A URL key is ignored unless its toggle above is enabled.</li>
                  </ul>
                </div>
              </div>
            </details>
          </div>
        )}

        <div className="p-3 bg-slate-50 rounded-lg border space-y-3">
          <h4 className="font-semibold text-sm">Image layout</h4>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Aspect ratio</Label>
              <select
                value={configFields.image_aspect_ratio || "4:3"}
                onChange={(e) => update({ image_aspect_ratio: e.target.value })}
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                data-testid="card-image-aspect"
              >
                <option value="1:1">1:1 (square)</option>
                <option value="4:3">4:3</option>
                <option value="3:2">3:2</option>
                <option value="16:9">16:9</option>
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm pt-6">
              <input
                type="checkbox"
                checked={!!configFields.image_rounded}
                onChange={(e) => update({ image_rounded: e.target.checked })}
                data-testid="card-image-rounded"
              />
              Rounded corners
            </label>
          </div>
        </div>
      </>
    );
  }

  if (moduleType === "products") {
    return <ProductsConfigFields configFields={configFields} update={update} />;
  }

  if (moduleType === "property_article") {
    return (
      <PropertyArticleConfigFields
        config={configFields}
        onChange={setConfigFields}
        instanceId={instanceId}
      />
    );
  }

  if (moduleType === "basket") {
    return (
      <div className="space-y-3">
        <div>
          <label className="text-xs font-medium mb-1 block">Section title</label>
          <input type="text" value={configFields.title || ""}
            onChange={(e) => update({ title: e.target.value })}
            placeholder="Your Basket"
            className="w-full h-9 px-3 border border-input rounded text-sm"
            data-testid="basket-config-title" />
        </div>
        <div>
          <label className="text-xs font-medium mb-1 block">Subtitle</label>
          <input type="text" value={configFields.subtitle || ""}
            onChange={(e) => update({ subtitle: e.target.value })}
            placeholder="Optional blurb"
            className="w-full h-9 px-3 border border-input rounded text-sm"
            data-testid="basket-config-subtitle" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium mb-1 block">Checkout URL</label>
            <input type="text" value={configFields.checkout_url || "/checkout"}
              onChange={(e) => update({ checkout_url: e.target.value })}
              className="w-full h-9 px-3 border border-input rounded text-sm"
              data-testid="basket-config-checkout-url" />
          </div>
          <div>
            <label className="text-xs font-medium mb-1 block">Continue-shopping URL</label>
            <input type="text" value={configFields.continue_url || "/"}
              onChange={(e) => update({ continue_url: e.target.value })}
              className="w-full h-9 px-3 border border-input rounded text-sm"
              data-testid="basket-config-continue-url" />
          </div>
        </div>
        <div>
          <label className="text-xs font-medium mb-1 block">Continue-shopping label</label>
          <input type="text" value={configFields.continue_label || "Continue shopping"}
            onChange={(e) => update({ continue_label: e.target.value })}
            className="w-full h-9 px-3 border border-input rounded text-sm"
            data-testid="basket-config-continue-label" />
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={!!configFields.hide_when_empty}
            onChange={(e) => update({ hide_when_empty: e.target.checked })}
            data-testid="basket-config-hide-empty" />
          Hide this module when the basket is empty
        </label>
      </div>
    );
  }

  if (moduleType === "checkout") {
    return (
      <div className="space-y-3">
        <div>
          <label className="text-xs font-medium mb-1 block">Section title</label>
          <input type="text" value={configFields.title || ""}
            onChange={(e) => update({ title: e.target.value })}
            placeholder="Checkout"
            className="w-full h-9 px-3 border border-input rounded text-sm"
            data-testid="checkout-config-title" />
        </div>
        <div>
          <label className="text-xs font-medium mb-1 block">Subtitle / reassurance</label>
          <textarea rows={2} value={configFields.subtitle || ""}
            onChange={(e) => update({ subtitle: e.target.value })}
            placeholder="You'll be redirected to Stripe's secure checkout to complete payment."
            className="w-full px-3 py-2 border border-input rounded text-sm"
            data-testid="checkout-config-subtitle" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium mb-1 block">Empty-basket URL</label>
            <input type="text" value={configFields.empty_url || "/"}
              onChange={(e) => update({ empty_url: e.target.value })}
              className="w-full h-9 px-3 border border-input rounded text-sm"
              data-testid="checkout-config-empty-url" />
          </div>
          <div>
            <label className="text-xs font-medium mb-1 block">Empty-basket link label</label>
            <input type="text" value={configFields.empty_label || "Continue shopping"}
              onChange={(e) => update({ empty_label: e.target.value })}
              className="w-full h-9 px-3 border border-input rounded text-sm"
              data-testid="checkout-config-empty-label" />
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Stripe payments handled through the pre-configured sandbox — no extra keys needed.
          Test card <code>4242 4242 4242 4242</code>, any future expiry, any CVC.
        </p>
      </div>
    );
  }

  if (moduleType === "milestones") {
    return (
      <div className="space-y-3">
        <div>
          <label className="text-xs font-medium mb-1 block">Heading</label>
          <input type="text" value={configFields.heading || ""}
            onChange={(e) => update({ heading: e.target.value })}
            placeholder="My Property Dashboard"
            className="w-full h-9 px-3 border border-input rounded text-sm"
            data-testid="milestones-config-heading" />
        </div>
        <div>
          <label className="text-xs font-medium mb-1 block">Subheading</label>
          <textarea rows={2} value={configFields.subheading || ""}
            onChange={(e) => update({ subheading: e.target.value })}
            placeholder="Track milestones and progress for each of your properties."
            className="w-full px-3 py-2 border border-input rounded text-sm"
            data-testid="milestones-config-subheading" />
        </div>
        <div>
          <label className="text-xs font-medium mb-1 block">Default milestone checklist</label>
          <textarea rows={10} value={(configFields.default_milestones || []).join("\n")}
            onChange={(e) => update({
              default_milestones: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean),
            })}
            placeholder={"Upload listing photos\nObtain EPC\nAccept an offer\n…"}
            className="w-full px-3 py-2 border border-input rounded text-sm font-mono"
            data-testid="milestones-config-default-list" />
          <p className="text-xs text-muted-foreground mt-1">
            One milestone per line. This checklist is automatically added to every new property the moment it's created.
          </p>
        </div>
        <div>
          <label className="text-xs font-medium mb-1 block">Reminder threshold (days)</label>
          <input type="number" min={1} value={configFields.reminder_threshold_days ?? 3}
            onChange={(e) => update({ reminder_threshold_days: Number(e.target.value) || 3 })}
            className="w-32 h-9 px-3 border border-input rounded text-sm"
            data-testid="milestones-config-reminder-threshold" />
          <p className="text-xs text-muted-foreground mt-1">
            A pulsing dot appears on the agent's Property Dashboard once a milestone has sat "Not started" for this many days.
          </p>
        </div>
      </div>
    );
  }

  if (moduleType === "capture_property_form") {
    return (
      <div className="space-y-3">
        <div>
          <label className="text-xs font-medium mb-1 block">Heading</label>
          <input type="text" value={configFields.heading || ""}
            onChange={(e) => update({ heading: e.target.value })}
            placeholder="List Your Property"
            className="w-full h-9 px-3 border border-input rounded text-sm"
            data-testid="capture-property-config-heading" />
        </div>
        <div>
          <label className="text-xs font-medium mb-1 block">Subheading</label>
          <textarea rows={2} value={configFields.subheading || ""}
            onChange={(e) => update({ subheading: e.target.value })}
            placeholder="Tell us about your property…"
            className="w-full px-3 py-2 border border-input rounded text-sm"
            data-testid="capture-property-config-subheading" />
        </div>
        <div>
          <label className="text-xs font-medium mb-1 block">Submit button text</label>
          <input type="text" value={configFields.submit_button_text || ""}
            onChange={(e) => update({ submit_button_text: e.target.value })}
            placeholder="Submit Property"
            className="w-full h-9 px-3 border border-input rounded text-sm"
            data-testid="capture-property-config-submit-text" />
        </div>
        <div>
          <label className="text-xs font-medium mb-1 block">Success heading</label>
          <input type="text" value={configFields.success_heading ?? "Thanks — check your inbox"}
            onChange={(e) => update({ success_heading: e.target.value })}
            className="w-full h-9 px-3 border border-input rounded text-sm"
            data-testid="capture-property-config-success-heading" />
        </div>
        <div>
          <label className="text-xs font-medium mb-1 block">Success message</label>
          <textarea rows={3} value={configFields.success_message ?? "We've emailed you a link to set a password and finish publishing your listing. If you submitted more than one property, one link unlocks all of them."}
            onChange={(e) => update({ success_message: e.target.value })}
            className="w-full px-3 py-2 border border-input rounded text-sm"
            data-testid="capture-property-config-success-message" />
        </div>
        <div>
          <label className="text-xs font-medium mb-1 block">Secondary message (shown below the Register with Google button)</label>
          <textarea rows={2} value={configFields.success_message_2 ?? "No rush — you can register whenever suits you and pick up your listing right where you left off."}
            onChange={(e) => update({ success_message_2: e.target.value })}
            className="w-full px-3 py-2 border border-input rounded text-sm"
            data-testid="capture-property-config-success-message-2" />
        </div>
        <div>
          <label className="text-xs font-medium mb-1 block">Redirect after submit (optional)</label>
          <select
            value={configFields.redirect_slug || ""}
            onChange={(e) => update({ redirect_slug: e.target.value })}
            className="w-full h-9 px-3 border border-input rounded text-sm bg-background"
            data-testid="capture-property-config-redirect-slug-picker"
          >
            <option value="">Show success message instead (no redirect)</option>
            {allPages.modular?.length > 0 && (
              <optgroup label="Modular Pages">
                {allPages.modular.map((page) => (
                  <option key={page.composition_id} value={page.page_slug === null ? "/" : `/${page.page_slug}`}>
                    {page.page_title} ({page.page_slug === null ? "/" : `/${page.page_slug}`})
                  </option>
                ))}
              </optgroup>
            )}
            {allPages.preset?.length > 0 && (
              <optgroup label="Preset Pages">
                {allPages.preset.map((page) => (
                  <option key={page.nav_id} value={page.path}>
                    {page.title} ({page.path})
                  </option>
                ))}
              </optgroup>
            )}
            {allPages.custom?.length > 0 && (
              <optgroup label="Custom Pages">
                {allPages.custom.map((page) => (
                  <option key={page.page_id} value={`/${page.slug}`}>
                    {page.title} (/{page.slug})
                  </option>
                ))}
              </optgroup>
            )}
          </select>
          <input
            type="text"
            value={configFields.redirect_slug || ""}
            onChange={(e) => update({ redirect_slug: e.target.value })}
            placeholder="…or type a custom path/URL, e.g. https://example.com"
            className="w-full h-9 px-3 mt-2 border border-input rounded text-sm"
            data-testid="capture-property-config-redirect-slug"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Pick a page above, or type any internal path (e.g. /property-admin/dashboard) or
            full external URL. Leave blank to show the success message above instead.
          </p>
        </div>
        <div>
          <ColorPickerInput
            label="Form background color (behind the fields)"
            compact
            value={configFields.form_bg_color || ""}
            onChange={(v) => update({ form_bg_color: v })}
            testId="capture-property-config-form-bg-color"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Colours just the form card. To colour the area around the form, use &quot;Section
            background&quot; further down.
          </p>
        </div>
        <div className="pt-4 border-t space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Valuation Email &amp; Post-Submit CTA
          </p>
          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={!!configFields.bypass_valuation}
              onChange={(e) => update({ bypass_valuation: e.target.checked })}
              data-testid="capture-property-config-bypass-valuation"
            />
            <span>
              Bypass valuation
              <span className="block text-xs text-muted-foreground font-normal">
                Skip the HM Land Registry lookup, AI estimate, PDF report and email on submit — just save
                the lead and show the success heading/message above. The fields in this section are
                ignored while this is on.
              </span>
            </span>
          </label>
          <div>
            <label className="text-xs font-medium mb-1 block">Price estimate bias (%)</label>
            <input type="number" step="1" min={-50} max={50} value={configFields.bias_percentage ?? 0}
              onChange={(e) => {
                const raw = e.target.value;
                // Keep intermediate states like "" or "-" as-is instead of
                // coercing through Number() on every keystroke — that was
                // collapsing a lone "-" to 0, so typing "-3" produced "03"
                // (a positive number) instead of -3.
                if (raw === "" || raw === "-") { update({ bias_percentage: raw }); return; }
                const parsed = Number(raw);
                update({ bias_percentage: Number.isNaN(parsed) ? 0 : parsed });
              }}
              onBlur={() => {
                if (configFields.bias_percentage === "" || configFields.bias_percentage === "-") {
                  update({ bias_percentage: 0 });
                }
              }}
              className="w-32 h-9 px-3 border border-input rounded text-sm"
              data-testid="capture-property-config-bias-percentage" />
            <p className="text-xs text-muted-foreground mt-1">
              Nudges the AI's HM Land Registry-grounded price estimate up (positive) or down (negative) by this
              percentage before it's emailed and stored in the valuation report. 0 = no adjustment.
            </p>
          </div>
          <div>
            <label className="text-xs font-medium mb-1 block">In-email CTA button text</label>
            <input type="text" value={configFields.in_email_cta_copy || ""}
              onChange={(e) => update({ in_email_cta_copy: e.target.value })}
              placeholder="e.g. Book a free valuation call"
              className="w-full h-9 px-3 border border-input rounded text-sm"
              data-testid="capture-property-config-in-email-cta-copy" />
          </div>
          <div>
            <label className="text-xs font-medium mb-1 block">In-email CTA link</label>
            <select
              value={configFields.in_email_cta_url || ""}
              onChange={(e) => update({ in_email_cta_url: e.target.value })}
              className="w-full h-9 px-3 border border-input rounded text-sm bg-background"
              data-testid="capture-property-config-in-email-cta-url-picker"
            >
              <option value="">No CTA (leave blank)</option>
              {allPages.modular?.length > 0 && (
                <optgroup label="Modular Pages">
                  {allPages.modular.map((page) => (
                    <option key={page.composition_id} value={page.page_slug === null ? "/" : `/${page.page_slug}`}>
                      {page.page_title} ({page.page_slug === null ? "/" : `/${page.page_slug}`})
                    </option>
                  ))}
                </optgroup>
              )}
              {allPages.preset?.length > 0 && (
                <optgroup label="Preset Pages">
                  {allPages.preset.map((page) => (
                    <option key={page.nav_id} value={page.path}>{page.title} ({page.path})</option>
                  ))}
                </optgroup>
              )}
              {allPages.custom?.length > 0 && (
                <optgroup label="Custom Pages">
                  {allPages.custom.map((page) => (
                    <option key={page.page_id} value={`/${page.slug}`}>{page.title} (/{page.slug})</option>
                  ))}
                </optgroup>
              )}
            </select>
            <input type="text" value={configFields.in_email_cta_url || ""}
              onChange={(e) => update({ in_email_cta_url: e.target.value })}
              placeholder="…or type a custom path/URL, e.g. https://example.com/book"
              className="w-full h-9 px-3 mt-2 border border-input rounded text-sm"
              data-testid="capture-property-config-in-email-cta-url" />
          </div>
          <div>
            <label className="text-xs font-medium mb-1 block">Post-submit CTA button text</label>
            <input type="text" value={configFields.post_submit_cta_copy || ""}
              onChange={(e) => update({ post_submit_cta_copy: e.target.value })}
              placeholder="e.g. See how we can help"
              className="w-full h-9 px-3 border border-input rounded text-sm"
              data-testid="capture-property-config-post-submit-cta-copy" />
          </div>
          <div>
            <label className="text-xs font-medium mb-1 block">Post-submit CTA link</label>
            <select
              value={configFields.post_submit_cta_url || ""}
              onChange={(e) => update({ post_submit_cta_url: e.target.value })}
              className="w-full h-9 px-3 border border-input rounded text-sm bg-background"
              data-testid="capture-property-config-post-submit-cta-url-picker"
            >
              <option value="">No CTA (leave blank)</option>
              {allPages.modular?.length > 0 && (
                <optgroup label="Modular Pages">
                  {allPages.modular.map((page) => (
                    <option key={page.composition_id} value={page.page_slug === null ? "/" : `/${page.page_slug}`}>
                      {page.page_title} ({page.page_slug === null ? "/" : `/${page.page_slug}`})
                    </option>
                  ))}
                </optgroup>
              )}
              {allPages.preset?.length > 0 && (
                <optgroup label="Preset Pages">
                  {allPages.preset.map((page) => (
                    <option key={page.nav_id} value={page.path}>{page.title} ({page.path})</option>
                  ))}
                </optgroup>
              )}
              {allPages.custom?.length > 0 && (
                <optgroup label="Custom Pages">
                  {allPages.custom.map((page) => (
                    <option key={page.page_id} value={`/${page.slug}`}>{page.title} (/{page.slug})</option>
                  ))}
                </optgroup>
              )}
            </select>
            <input type="text" value={configFields.post_submit_cta_url || ""}
              onChange={(e) => update({ post_submit_cta_url: e.target.value })}
              placeholder="…or type a custom path/URL, e.g. https://example.com/book"
              className="w-full h-9 px-3 mt-2 border border-input rounded text-sm"
              data-testid="capture-property-config-post-submit-cta-url" />
            <p className="text-xs text-muted-foreground mt-1">
              Shown as an extra button on the thank-you screen, alongside the existing "Register with Google" CTA.
            </p>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Which fields appear on this form (and their section grouping) is controlled from
          CMS → Fields → tick &quot;Include&quot; on the &quot;List your property form&quot; column.
        </p>
      </div>
    );
  }

  if (moduleType === "house_prices_tool" || moduleType === "planning_applications_tool" || moduleType === "planning_appeals_tool") {
    const testPrefix = moduleType === "house_prices_tool" ? "house-prices" : moduleType === "planning_applications_tool" ? "planning-applications" : "planning-appeals";
    return (
      <div className="space-y-3">
        <div>
          <label className="text-xs font-medium mb-1 block">Heading</label>
          <input type="text" value={configFields.heading || ""}
            onChange={(e) => update({ heading: e.target.value })}
            className="w-full h-9 px-3 border border-input rounded text-sm"
            data-testid={`${testPrefix}-config-heading`} />
        </div>
        <div>
          <label className="text-xs font-medium mb-1 block">Subheading</label>
          <textarea rows={2} value={configFields.subheading || ""}
            onChange={(e) => update({ subheading: e.target.value })}
            className="w-full px-3 py-2 border border-input rounded text-sm"
            data-testid={`${testPrefix}-config-subheading`} />
        </div>
        <div>
          <label className="text-xs font-medium mb-1 block">CTA button text <span className="text-muted-foreground">(leave blank to hide)</span></label>
          <input type="text" value={configFields.cta_text || ""}
            onChange={(e) => update({ cta_text: e.target.value })}
            placeholder="Get in touch"
            className="w-full h-9 px-3 border border-input rounded text-sm"
            data-testid={`${testPrefix}-config-cta-text`} />
        </div>
        <div>
          <label className="text-xs font-medium mb-1 block">CTA links to</label>
          <select
            value={configFields.cta_page_slug || ""}
            onChange={(e) => update({ cta_page_slug: e.target.value })}
            className="w-full h-9 px-3 border border-input rounded text-sm bg-background"
            data-testid={`${testPrefix}-config-cta-page-slug`}
          >
            <option value="">Select a page</option>
            {allPages.modular?.length > 0 && (
              <optgroup label="Modular Pages">
                {allPages.modular.map((page) => (
                  <option key={page.composition_id} value={page.page_slug === null ? "/" : (page.page_slug || "/")}>
                    {page.page_title} ({page.page_slug === null ? "/" : `/${page.page_slug}`})
                  </option>
                ))}
              </optgroup>
            )}
            {allPages.preset?.length > 0 && (
              <optgroup label="Preset Pages">
                {allPages.preset.map((page) => (
                  <option key={page.nav_id} value={page.path}>
                    {page.title} ({page.path})
                  </option>
                ))}
              </optgroup>
            )}
            {allPages.custom?.length > 0 && (
              <optgroup label="Custom Pages">
                {allPages.custom.map((page) => (
                  <option key={page.page_id} value={`/${page.slug}`}>
                    {page.title} (/{page.slug})
                  </option>
                ))}
              </optgroup>
            )}
          </select>
        </div>
        {moduleType === "house_prices_tool" && (
          <div className="space-y-3 pt-3 border-t">
            <p className="text-xs font-medium text-muted-foreground">
              Thank-you card CTA — shown after a visitor unlocks their results, below the
              "check your email" message.
            </p>
            <div>
              <label className="text-xs font-medium mb-1 block">CTA button text <span className="text-muted-foreground">(leave blank to hide)</span></label>
              <input type="text" value={configFields.thankyou_cta_text || ""}
                onChange={(e) => update({ thankyou_cta_text: e.target.value })}
                placeholder="Browse our properties"
                className="w-full h-9 px-3 border border-input rounded text-sm"
                data-testid="house-prices-config-thankyou-cta-text" />
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block">CTA links to</label>
              <select
                value={configFields.thankyou_cta_page_slug || ""}
                onChange={(e) => update({ thankyou_cta_page_slug: e.target.value })}
                className="w-full h-9 px-3 border border-input rounded text-sm bg-background"
                data-testid="house-prices-config-thankyou-cta-page-slug"
              >
                <option value="">Select a page</option>
                {allPages.modular?.length > 0 && (
                  <optgroup label="Modular Pages">
                    {allPages.modular.map((page) => (
                      <option key={page.composition_id} value={page.page_slug ? `/${page.page_slug}` : "/"}>
                        {page.page_title} ({page.page_slug === null ? "/" : `/${page.page_slug}`})
                      </option>
                    ))}
                  </optgroup>
                )}
                {allPages.preset?.length > 0 && (
                  <optgroup label="Preset Pages">
                    {allPages.preset.map((page) => (
                      <option key={page.nav_id} value={page.path}>
                        {page.title} ({page.path})
                      </option>
                    ))}
                  </optgroup>
                )}
                {allPages.custom?.length > 0 && (
                  <optgroup label="Custom Pages">
                    {allPages.custom.map((page) => (
                      <option key={page.page_id} value={`/${page.slug}`}>
                        {page.title} (/{page.slug})
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
            </div>
          </div>
        )}
        {moduleType === "house_prices_tool" && (
          <div className="space-y-3 pt-3 border-t">
            <p className="text-xs font-medium text-muted-foreground">
              Email sender — used on the "house prices results" email sent after a visitor
              unlocks their results. Leave blank to use the site's default sender (Admin → System → Emails).
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium mb-1 block">Sender name</label>
                <input type="text" value={configFields.sender_name || ""}
                  onChange={(e) => update({ sender_name: e.target.value })}
                  placeholder="Sell Local Homes"
                  className="w-full h-9 px-3 border border-input rounded text-sm"
                  data-testid="house-prices-config-sender-name" />
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block">Sender email</label>
                <input type="email" value={configFields.sender_email || ""}
                  onChange={(e) => update({ sender_email: e.target.value })}
                  placeholder="hello@selllocalhomes.co.uk"
                  className="w-full h-9 px-3 border border-input rounded text-sm"
                  data-testid="house-prices-config-sender-email" />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (moduleType === "pricing_table") {
    return <PricingTableConfigFields configFields={configFields} update={update} allPages={allPages} />;
  }

  if (moduleType === "cards_grid") {
    return <CardsGridConfigFields configFields={configFields} update={update} allPages={allPages} />;
  }

  if (moduleType === "advert") {
    return <AdvertConfigFields configFields={configFields} update={update} />;
  }

  return (
    <p className="text-sm text-muted-foreground">
      This module type has no configurable fields.
    </p>
  );
}

// ---------------------------------------------------------------------------
// Pricing Table config — admin manages an arbitrary list of tiers (name,
// price, billing suffix, features, optional "highlighted" tier + CTA).
// ---------------------------------------------------------------------------
function PricingTableConfigFields({ configFields, update, allPages = { modular: [], preset: [], custom: [] } }) {
  const tiers = Array.isArray(configFields.tiers) ? configFields.tiers : [];

  const patchTier = (idx, patch) => {
    update({ tiers: tiers.map((t, i) => (i === idx ? { ...t, ...patch } : t)) });
  };
  const addTier = () => {
    update({
      tiers: [...tiers, {
        name: "New Plan", price: "0", interval: "/mo", description: "",
        features: ["Feature one"], highlighted: false, badge_text: "",
        cta_text: "Choose Plan", cta_link: "/contact",
      }],
    });
  };
  const removeTier = (idx) => update({ tiers: tiers.filter((_, i) => i !== idx) });
  const moveTier = (idx, delta) => {
    const target = idx + delta;
    if (target < 0 || target >= tiers.length) return;
    const next = [...tiers];
    const [moved] = next.splice(idx, 1);
    next.splice(target, 0, moved);
    update({ tiers: next });
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Heading</Label>
        <Input value={configFields.heading || ""} onChange={(e) => update({ heading: e.target.value })} placeholder="Choose Your Plan" data-testid="pricing-table-heading" />
      </div>
      <TextStyleControls
        label="Heading style"
        prefix="heading_"
        config={configFields}
        update={update}
        preview={configFields.heading || "Choose Your Plan"}
        compact
      />
      <div className="space-y-2">
        <Label>Subheading</Label>
        <Input value={configFields.subheading || ""} onChange={(e) => update({ subheading: e.target.value })} placeholder="Pick the plan that's right for you" data-testid="pricing-table-subheading" />
      </div>
      <TextStyleControls
        label="Subheading style"
        prefix="subheading_"
        config={configFields}
        update={update}
        preview={configFields.subheading || "Pick the plan that's right for you"}
        compact
      />
      <TextStyleControls
        label="Tier name heading style (applies to every plan's name)"
        prefix="tier_name_"
        config={configFields}
        update={update}
        preview={tiers[0]?.name || "Bronze"}
        compact
      />
      <div className="grid grid-cols-2 gap-3 pt-2 border-t">
        <ColorPickerInput label="Card background" compact value={configFields.card_bg_color || ""} onChange={(v) => update({ card_bg_color: v })} />
        <ColorPickerInput label="Highlighted card background" compact value={configFields.highlight_bg_color || ""} onChange={(v) => update({ highlight_bg_color: v })} />
        <ColorPickerInput label="Highlighted border / badge" compact value={configFields.highlight_accent_color || ""} onChange={(v) => update({ highlight_accent_color: v })} />
        <ColorPickerInput label="CTA button background" compact value={configFields.cta_bg_color || ""} onChange={(v) => update({ cta_bg_color: v })} />
        <ColorPickerInput label="Price color" compact value={configFields.price_color || ""} onChange={(v) => update({ price_color: v })} />
      </div>
      <div className="space-y-1 pt-1">
        <Label className="text-[11px]">Price font</Label>
        <select
          value={configFields.price_font_family || ""}
          onChange={(e) => update({ price_font_family: e.target.value })}
          className="w-full h-9 px-3 border border-input rounded text-sm"
          data-testid="pricing-table-price-font"
        >
          <option value="">Default (serif)</option>
          <option value="'Playfair Display', serif">Playfair Display (serif)</option>
          <option value="'Lora', serif">Lora (serif)</option>
          <option value="'Inter', sans-serif">Inter (sans-serif)</option>
          <option value="'Montserrat', sans-serif">Montserrat</option>
          <option value="'Poppins', sans-serif">Poppins</option>
          <option value="'Roboto', sans-serif">Roboto</option>
          <option value="ui-monospace, monospace">Monospace</option>
        </select>
      </div>

      <div className="pt-3 border-t space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-sm" style={{ fontSize: "0.875rem" }}>Pricing Tiers</h3>
          <button
            type="button"
            onClick={addTier}
            className="text-xs px-2 py-1 bg-primary text-primary-foreground rounded hover:opacity-90"
            data-testid="pricing-table-add-tier"
          >
            + Add Tier
          </button>
        </div>
        {tiers.length === 0 && (
          <p className="text-xs text-muted-foreground italic text-center py-3">
            No tiers yet. Click "+ Add Tier" to add one.
          </p>
        )}
        {tiers.map((tier, idx) => (
          <div key={idx} className="border rounded-lg p-3 space-y-3 bg-muted/20" data-testid={`pricing-table-tier-${idx}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-medium text-muted-foreground">Tier {idx + 1}</span>
              <div className="flex items-center gap-1">
                <button type="button" className="text-xs px-1.5 py-0.5 border rounded hover:bg-muted disabled:opacity-30" disabled={idx === 0} onClick={() => moveTier(idx, -1)} data-testid={`pricing-table-tier-up-${idx}`}>▲</button>
                <button type="button" className="text-xs px-1.5 py-0.5 border rounded hover:bg-muted disabled:opacity-30" disabled={idx === tiers.length - 1} onClick={() => moveTier(idx, 1)} data-testid={`pricing-table-tier-down-${idx}`}>▼</button>
                <button type="button" className="text-xs px-1.5 py-0.5 border border-red-300 text-red-600 rounded hover:bg-red-50" onClick={() => removeTier(idx)} data-testid={`pricing-table-tier-remove-${idx}`}>Delete</button>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-1">
                <Label className="text-[11px]">Tier name</Label>
                <Input value={tier.name || ""} onChange={(e) => patchTier(idx, { name: e.target.value })} data-testid={`pricing-table-tier-name-${idx}`} />
              </div>
              <div className="space-y-1">
                <Label className="text-[11px]">Price</Label>
                <Input value={tier.price || ""} onChange={(e) => patchTier(idx, { price: e.target.value })} placeholder="£19" data-testid={`pricing-table-tier-price-${idx}`} />
              </div>
              <div className="space-y-1">
                <Label className="text-[11px]">Billing suffix</Label>
                <Input value={tier.interval || ""} onChange={(e) => patchTier(idx, { interval: e.target.value })} placeholder="/mo" data-testid={`pricing-table-tier-interval-${idx}`} />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-[11px]">Tier image (optional — shown at the top of the card, spans most of its width)</Label>
              {tier.image_url && (
                <img src={tier.image_url} alt="preview" className="w-full max-w-xs aspect-video object-cover rounded border" />
              )}
              <ImageUpload
                onUpload={(url) => patchTier(idx, { image_url: url })}
                multiple={false}
                label={tier.image_url ? "Replace image" : "Upload image"}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-[11px]">Description (optional)</Label>
              <Input value={tier.description || ""} onChange={(e) => patchTier(idx, { description: e.target.value })} placeholder="Short one-line summary" data-testid={`pricing-table-tier-description-${idx}`} />
            </div>
            <div className="space-y-1">
              <Label className="text-[11px]">Features — one per line</Label>
              <Textarea
                rows={4}
                value={(tier.features || []).join("\n")}
                onChange={(e) => patchTier(idx, { features: e.target.value.split("\n") })}
                placeholder={"Feature one\nFeature two"}
                data-testid={`pricing-table-tier-features-${idx}`}
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-[11px]">CTA button text</Label>
                <Input value={tier.cta_text || ""} onChange={(e) => patchTier(idx, { cta_text: e.target.value })} placeholder="Choose Plan" data-testid={`pricing-table-tier-cta-text-${idx}`} />
              </div>
              <div className="space-y-1">
                <Label className="text-[11px]">CTA link</Label>
                <select
                  value={tier.cta_link || ""}
                  onChange={(e) => patchTier(idx, { cta_link: e.target.value })}
                  className="w-full h-9 px-3 border border-input rounded text-sm bg-background"
                  data-testid={`pricing-table-tier-cta-link-${idx}`}
                >
                  <option value="">Select a page</option>
                  {allPages.modular?.length > 0 && (
                    <optgroup label="Modular Pages">
                      {allPages.modular.map((page) => (
                        <option key={page.composition_id} value={page.page_slug ? `/${page.page_slug}` : "/"}>
                          {page.page_title} ({page.page_slug === null ? "/" : `/${page.page_slug}`})
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {allPages.preset?.length > 0 && (
                    <optgroup label="Preset Pages">
                      {allPages.preset.map((page) => (
                        <option key={page.nav_id} value={page.path}>
                          {page.title} ({page.path})
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {allPages.custom?.length > 0 && (
                    <optgroup label="Custom Pages">
                      {allPages.custom.map((page) => (
                        <option key={page.page_id} value={`/${page.slug}`}>
                          {page.title} (/{page.slug})
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
              </div>
            </div>
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={!!tier.highlighted}
                onChange={(e) => patchTier(idx, { highlighted: e.target.checked })}
                data-testid={`pricing-table-tier-highlighted-${idx}`}
              />
              Highlight this tier (recommended / most popular)
            </label>
            {tier.highlighted && (
              <div className="space-y-1">
                <Label className="text-[11px]">Badge text</Label>
                <Input value={tier.badge_text || ""} onChange={(e) => patchTier(idx, { badge_text: e.target.value })} placeholder="Most Popular" data-testid={`pricing-table-tier-badge-${idx}`} />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Cards Grid config — admin adds cards one at a time (up to 16 = 4 per row ×
// 4 rows). Each card has its own heading, sub-heading, rich text, CTA,
// background image and border style. No per-card font controls — typography
// follows the global Site Styles, same as the module heading/subheading.
// ---------------------------------------------------------------------------
const MAX_CARDS_GRID_CARDS = 16;
const CARDS_GRID_PER_ROW = 4;
const CARDS_GRID_SHADOW_PRESETS = {
  none: "",
  soft: "0 2px 8px rgba(0,0,0,0.12)",
  medium: "0 6px 16px rgba(0,0,0,0.18)",
  strong: "0 12px 28px rgba(0,0,0,0.3)",
};
const CARDS_GRID_STYLE_FIELDS = [
  "border_width", "border_style", "border_color", "border_radius",
  "background_image_opacity", "overlay_opacity", "card_shadow",
];

const MAX_ADVERT_CARDS = 4;
const ADVERT_CARD_POSITIONS = [
  { value: "above", label: "Above image" },
  { value: "above-overlap", label: "Above image, dips into it" },
  { value: "center-overlay", label: "Overlaid, centred on image" },
  { value: "bottom-overlay", label: "Overlaid, bottom-centred on image" },
  { value: "below", label: "Below image" },
  { value: "below-overlap", label: "Below image, overlaps up into it" },
];

/**
 * "Advert" module — a self-contained, portrait social-ad-style graphic:
 * a background image (zoom/opacity/blur/height adjustable), a company
 * logo pill fixed top-left over that image, and up to MAX_ADVERT_CARDS
 * repeatable rich-text "cards" an admin can place above/below/over the
 * image — e.g. a headline pill above and a price-comparison + CTA card
 * overlapping the bottom, matching a typical property-marketing ad.
 */
function AdvertConfigFields({ configFields, update }) {
  const { styles: siteStyles } = useSiteStyles();
  const cards = Array.isArray(configFields.cards) ? configFields.cards : [];

  const patchCard = (idx, patch) => {
    update({ cards: cards.map((c, i) => (i === idx ? { ...c, ...patch } : c)) });
  };
  const addCard = () => {
    if (cards.length >= MAX_ADVERT_CARDS) return;
    update({
      cards: [...cards, {
        id: (crypto.randomUUID && crypto.randomUUID()) || `card-${Date.now()}`,
        position: "bottom-overlay",
        bg_color: "#ffffff", corner_radius: 20,
        border_width: 0, border_color: "#e5e7eb",
        max_width: "", padding: 24, overlap_px: 48,
        card_shadow: "none",
        content_html: "",
      }],
    });
  };
  const removeCard = (idx) => update({ cards: cards.filter((_, i) => i !== idx) });
  const moveCard = (idx, delta) => {
    const target = idx + delta;
    if (target < 0 || target >= cards.length) return;
    const next = [...cards];
    const [moved] = next.splice(idx, 1);
    next.splice(target, 0, moved);
    update({ cards: next });
  };

  return (
    <div className="space-y-4">
      <div className="space-y-3 pb-3 border-b">
        <h3 className="font-semibold text-sm">Outer frame</h3>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className="flex items-center gap-1.5 text-xs mb-1">
              <input type="checkbox" checked={configFields.container_bg_color === "transparent"}
                onChange={(e) => {
                  if (e.target.checked) {
                    update({ container_bg_color: "transparent", container_bg_color_last: configFields.container_bg_color !== "transparent" ? configFields.container_bg_color : configFields.container_bg_color_last });
                  } else {
                    update({ container_bg_color: configFields.container_bg_color_last || "#102a3d" });
                  }
                }}
                data-testid="advert-container-transparent-checkbox" />
              No background (transparent)
            </label>
            {configFields.container_bg_color !== "transparent" && (
              <ColorPickerInput label="Background colour" value={configFields.container_bg_color || "#102a3d"}
                onChange={(v) => update({ container_bg_color: v, container_bg_color_last: v })} />
            )}
          </div>
          <div className="space-y-1">
            <Label className="text-[11px]">Max width: {configFields.container_max_width || 420}px</Label>
            <input type="range" min="280" max="800" step="10" value={configFields.container_max_width || 420}
              onChange={(e) => update({ container_max_width: parseInt(e.target.value) })} className="w-full" />
          </div>
          <div className="space-y-1">
            <Label className="text-[11px]">Padding: {configFields.container_padding ?? 20}px</Label>
            <input type="range" min="0" max="60" step="2" value={configFields.container_padding ?? 20}
              onChange={(e) => update({ container_padding: parseInt(e.target.value) })} className="w-full" />
          </div>
          <div className="space-y-1">
            <Label className="text-[11px]">Corner radius: {configFields.container_radius ?? 16}px</Label>
            <input type="range" min="0" max="40" step="2" value={configFields.container_radius ?? 16}
              onChange={(e) => update({ container_radius: parseInt(e.target.value) })} className="w-full" />
          </div>
        </div>
      </div>

      <div className="space-y-3 pb-3 border-b">
        <h3 className="font-semibold text-sm">Background image</h3>
        {configFields.background_image && (
          <div className="relative w-full max-w-xs" style={{ height: `${configFields.image_height || 320}px` }}>
            <img
              src={resolveImageUrl(configFields.background_image)}
              alt="preview"
              className="w-full h-full object-cover rounded border"
              style={{
                transform: `scale(${(configFields.image_zoom || 100) / 100})`,
                opacity: (configFields.image_opacity ?? 100) / 100,
                filter: [
                  configFields.image_blur ? `blur(${configFields.image_blur}px)` : null,
                  (configFields.image_brightness ?? 100) !== 100 ? `brightness(${(configFields.image_brightness ?? 100) / 100})` : null,
                ].filter(Boolean).join(" ") || undefined,
              }}
            />
            {buildGradientCss(configFields.image_gradient, configFields.image_gradient_color, configFields.image_gradient_opacity) && (
              <div className="absolute inset-0 rounded pointer-events-none" style={{ background: buildGradientCss(configFields.image_gradient, configFields.image_gradient_color, configFields.image_gradient_opacity) }} />
            )}
            <Button type="button" variant="destructive" size="sm" className="absolute top-1 right-1"
              onClick={() => update({ background_image: "" })}>Remove</Button>
          </div>
        )}
        <ImageUpload
          onUpload={(url) => update({ background_image: url })}
          multiple={false}
          label={configFields.background_image ? "Replace image" : "Upload image"}
          testId="advert-image-upload"
        />
        <div className="grid grid-cols-2 gap-3 pt-1">
          <div className="space-y-1">
            <Label className="text-[11px]">Reduce (image frame height): {configFields.image_height || 320}px</Label>
            <input type="range" min="120" max="600" step="10" value={configFields.image_height || 320}
              onChange={(e) => update({ image_height: parseInt(e.target.value) })} className="w-full"
              data-testid="advert-image-height-slider" />
          </div>
          <div className="space-y-1">
            <Label className="text-[11px]">Zoom: {configFields.image_zoom || 100}%</Label>
            <input type="range" min="50" max="250" step="5" value={configFields.image_zoom || 100}
              onChange={(e) => update({ image_zoom: parseInt(e.target.value) })} className="w-full"
              data-testid="advert-image-zoom-slider" />
          </div>
          <div className="space-y-1">
            <Label className="text-[11px]">Transparency: {configFields.image_opacity ?? 100}%</Label>
            <input type="range" min="0" max="100" step="5" value={configFields.image_opacity ?? 100}
              onChange={(e) => update({ image_opacity: parseInt(e.target.value) })} className="w-full"
              data-testid="advert-image-opacity-slider" />
          </div>
          <div className="space-y-1">
            <Label className="text-[11px]">Blur: {configFields.image_blur || 0}px</Label>
            <input type="range" min="0" max="20" step="1" value={configFields.image_blur || 0}
              onChange={(e) => update({ image_blur: parseInt(e.target.value) })} className="w-full"
              data-testid="advert-image-blur-slider" />
          </div>
          <div className="space-y-1">
            <Label className="text-[11px]">Brightness: {configFields.image_brightness ?? 100}%</Label>
            <input type="range" min="30" max="200" step="5" value={configFields.image_brightness ?? 100}
              onChange={(e) => update({ image_brightness: parseInt(e.target.value) })} className="w-full"
              data-testid="advert-image-brightness-slider" />
          </div>
        </div>

        <div className="space-y-2 pt-2 border-t">
          <Label className="text-[11px] font-medium">Gradient overlay</Label>
          <div className="flex gap-1.5 flex-wrap">
            {GRADIENT_DIRECTIONS.map((opt) => (
              <Button key={opt.value} type="button" size="sm"
                variant={(configFields.image_gradient || "none") === opt.value ? "default" : "outline"}
                onClick={() => update({ image_gradient: opt.value })}
                className="text-xs" data-testid={`advert-image-gradient-${opt.value}`}
              >
                {opt.label}
              </Button>
            ))}
          </div>
          {(configFields.image_gradient || "none") !== "none" && (
            <div className="grid grid-cols-2 gap-2 pt-1">
              <ColorPickerInput compact label="Colour" value={configFields.image_gradient_color || "#000000"}
                onChange={(v) => update({ image_gradient_color: v })} />
              <div className="space-y-1">
                <Label className="text-[11px]">Strength: {configFields.image_gradient_opacity ?? 60}%</Label>
                <input type="range" min="0" max="100" step="5" value={configFields.image_gradient_opacity ?? 60}
                  onChange={(e) => update({ image_gradient_opacity: parseInt(e.target.value) })} className="w-full"
                  data-testid="advert-image-gradient-opacity-slider" />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="space-y-3 pb-3 border-b">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-sm">Company logo (top-left)</h3>
          <label className="flex items-center gap-1.5 text-xs">
            <input type="checkbox" checked={configFields.logo_enabled !== false}
              onChange={(e) => update({ logo_enabled: e.target.checked })}
              data-testid="advert-logo-enabled-checkbox" />
            Show
          </label>
        </div>
        {configFields.logo_enabled !== false && (
          <>
            <div className="flex items-center gap-3">
              {siteStyles?.companyLogo ? (
                <img src={resolveImageUrl(siteStyles.companyLogo)} alt="Company logo" className="h-14 w-14 object-contain rounded border bg-white p-1" data-testid="advert-logo-company-preview" />
              ) : (
                <div className="h-14 w-14 rounded border bg-muted flex items-center justify-center text-[10px] text-muted-foreground text-center px-1">No logo set</div>
              )}
              <p className="text-[11px] text-muted-foreground">
                Always matches your Company Logo from Admin → Site Styles — the same logo used in the site navigation — so the ad stays on-brand automatically.
              </p>
            </div>
            <div className="space-y-1">
              <Label className="text-[11px]">Logo text (optional)</Label>
              <Input value={configFields.logo_text || ""} onChange={(e) => update({ logo_text: e.target.value })}
                placeholder="Sell Local Homes" data-testid="advert-logo-text-input" />
            </div>
          </>
        )}
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-sm">Cards ({cards.length}/{MAX_ADVERT_CARDS})</h3>
          <button type="button" onClick={addCard} disabled={cards.length >= MAX_ADVERT_CARDS}
            className="text-xs px-2 py-1 bg-primary text-primary-foreground rounded hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="advert-add-card">
            + Add Card
          </button>
        </div>
        <p className="text-xs text-muted-foreground">
          Default is "Overlaid, bottom-centred on image" — a caption banner sitting directly on the photo. Pick other positions if you'd rather stack text above/below the image.
        </p>
        {cards.length === 0 && (
          <p className="text-xs text-muted-foreground italic text-center py-3">No cards yet. Click "+ Add Card" to add one.</p>
        )}
        {cards.map((card, idx) => (
          <div key={card.id || idx} className="border rounded-lg p-3 space-y-3 bg-muted/20" data-testid={`advert-card-${idx}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-medium text-muted-foreground">Card {idx + 1}</span>
              <div className="flex items-center gap-1">
                <button type="button" className="text-xs px-1.5 py-0.5 border rounded hover:bg-muted disabled:opacity-30" disabled={idx === 0} onClick={() => moveCard(idx, -1)} data-testid={`advert-card-up-${idx}`}>▲</button>
                <button type="button" className="text-xs px-1.5 py-0.5 border rounded hover:bg-muted disabled:opacity-30" disabled={idx === cards.length - 1} onClick={() => moveCard(idx, 1)} data-testid={`advert-card-down-${idx}`}>▼</button>
                <button type="button" className="text-xs px-1.5 py-0.5 border border-red-300 text-red-600 rounded hover:bg-red-50" onClick={() => removeCard(idx)} data-testid={`advert-card-remove-${idx}`}>Delete</button>
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-[11px]">Position</Label>
              <select value={card.position || "below"} onChange={(e) => patchCard(idx, { position: e.target.value })}
                className="w-full h-9 px-3 border border-input rounded text-sm bg-background"
                data-testid={`advert-card-position-${idx}`}>
                {ADVERT_CARD_POSITIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>

            {(card.position || "").includes("overlap") && (
              <div className="space-y-1">
                <Label className="text-[11px]">Overlap amount: {card.overlap_px ?? 48}px</Label>
                <input type="range" min="0" max="150" step="4" value={card.overlap_px ?? 48}
                  onChange={(e) => patchCard(idx, { overlap_px: parseInt(e.target.value) })} className="w-full"
                  data-testid={`advert-card-overlap-${idx}`} />
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <label className="flex items-center gap-1.5 text-xs mb-1">
                  <input type="checkbox" checked={card.bg_color === "transparent"}
                    onChange={(e) => {
                      if (e.target.checked) {
                        patchCard(idx, { bg_color: "transparent", bg_color_last: card.bg_color !== "transparent" ? card.bg_color : card.bg_color_last });
                      } else {
                        patchCard(idx, { bg_color: card.bg_color_last || "#ffffff" });
                      }
                    }}
                    data-testid={`advert-card-transparent-${idx}`} />
                  No background (transparent)
                </label>
                {card.bg_color !== "transparent" && (
                  <ColorPickerInput compact label="Background" value={card.bg_color || "#ffffff"}
                    onChange={(v) => patchCard(idx, { bg_color: v, bg_color_last: v })} />
                )}
              </div>
              <div className="space-y-1">
                <Label className="text-[11px]">Corner radius: {card.corner_radius ?? 20}px</Label>
                <input type="range" min="0" max="48" step="2" value={card.corner_radius ?? 20}
                  onChange={(e) => patchCard(idx, { corner_radius: parseInt(e.target.value) })} className="w-full" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-[11px]">Border width: {card.border_width ?? 0}px</Label>
                <input type="range" min="0" max="8" step="0.5" value={card.border_width ?? 0}
                  onChange={(e) => patchCard(idx, { border_width: parseFloat(e.target.value) })} className="w-full" />
              </div>
              {(card.border_width ?? 0) > 0 && (
                <ColorPickerInput compact label="Border colour" value={card.border_color || "#e5e7eb"}
                  onChange={(v) => patchCard(idx, { border_color: v })} />
              )}
            </div>
            <div className="space-y-1">
              <Label className="text-[11px]">Shadow</Label>
              <div className="flex gap-1.5">
                {Object.keys(CARDS_GRID_SHADOW_PRESETS).map((key) => (
                  <Button
                    key={key}
                    type="button"
                    size="sm"
                    variant={(card.card_shadow || "none") === key ? "default" : "outline"}
                    onClick={() => patchCard(idx, { card_shadow: key })}
                    className="text-xs capitalize flex-1"
                    data-testid={`advert-card-shadow-${key}-${idx}`}
                  >
                    {key}
                  </Button>
                ))}
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-[11px]">Max width (px, blank = fill frame)</Label>
              <Input type="number" min="0" value={card.max_width || ""} onChange={(e) => patchCard(idx, { max_width: e.target.value })}
                placeholder="e.g. 320" data-testid={`advert-card-max-width-${idx}`} />
            </div>
            <div className="space-y-1">
              <Label className="text-[11px]">Rich text content</Label>
              <div className="border rounded-md bg-background">
                <TiptapEditor
                  value={card.content_html || ""}
                  onChange={(html) => patchCard(idx, { content_html: html })}
                  placeholder="Write the card's content…"
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


function CardsGridConfigFields({ configFields, update, allPages = { modular: [], preset: [], custom: [] } }) {
  const cards = Array.isArray(configFields.cards) ? configFields.cards : [];

  const patchCard = (idx, patch) => {
    update({ cards: cards.map((c, i) => (i === idx ? { ...c, ...patch } : c)) });
  };
  const addCard = () => {
    if (cards.length >= MAX_CARDS_GRID_CARDS) return;
    update({
      cards: [...cards, {
        background_image: "", background_image_opacity: 100, overlay_opacity: 45, heading: "New Card", subheading: "", rich_text: "",
        cta_text: "", cta_url: "",
        border_width: 1, border_style: "solid", border_color: "#e5e7eb", border_radius: 12, card_shadow: "none",
      }],
    });
  };
  const duplicateCard = (idx) => {
    if (cards.length >= MAX_CARDS_GRID_CARDS) return;
    const copy = JSON.parse(JSON.stringify(cards[idx]));
    const next = [...cards];
    next.splice(idx + 1, 0, copy);
    update({ cards: next });
  };
  const removeCard = (idx) => update({ cards: cards.filter((_, i) => i !== idx) });
  const moveCard = (idx, delta) => {
    const target = idx + delta;
    if (target < 0 || target >= cards.length) return;
    const next = [...cards];
    const [moved] = next.splice(idx, 1);
    next.splice(target, 0, moved);
    update({ cards: next });
  };
  const copyStyleToAll = (idx) => {
    const styleProps = {};
    CARDS_GRID_STYLE_FIELDS.forEach((f) => { styleProps[f] = cards[idx][f]; });
    update({ cards: cards.map((c, i) => (i === idx ? c : { ...c, ...styleProps })) });
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Heading (optional)</Label>
        <Input value={configFields.heading || ""} onChange={(e) => update({ heading: e.target.value })} placeholder="e.g. Why sell with us" data-testid="cards-grid-heading" />
      </div>
      <TextStyleControls
        label="Heading style"
        prefix="heading_"
        config={configFields}
        update={update}
        preview={configFields.heading || "Why sell with us"}
        compact
      />
      <div className="space-y-2">
        <Label>Sub-heading (optional)</Label>
        <Input value={configFields.subheading || ""} onChange={(e) => update({ subheading: e.target.value })} placeholder="e.g. Everything included in every sale" data-testid="cards-grid-subheading" />
      </div>
      <TextStyleControls
        label="Sub-heading style"
        prefix="subheading_"
        config={configFields}
        update={update}
        preview={configFields.subheading || "Everything included in every sale"}
        compact
      />

      <div className="pt-3 border-t space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-sm">Cards ({cards.length}/{MAX_CARDS_GRID_CARDS})</h3>
          <button
            type="button"
            onClick={addCard}
            disabled={cards.length >= MAX_CARDS_GRID_CARDS}
            className="text-xs px-2 py-1 bg-primary text-primary-foreground rounded hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="cards-grid-add-card"
          >
            + Add Card
          </button>
        </div>
        <p className="text-xs text-muted-foreground">
          Cards wrap automatically — up to {CARDS_GRID_PER_ROW} per row, up to {MAX_CARDS_GRID_CARDS / CARDS_GRID_PER_ROW} rows. Each row's height fits its tallest card.
        </p>
        {cards.length === 0 && (
          <p className="text-xs text-muted-foreground italic text-center py-3">
            No cards yet. Click "+ Add Card" to add one.
          </p>
        )}
        {cards.map((card, idx) => (
          <div key={idx} className="border rounded-lg p-3 space-y-3 bg-muted/20" data-testid={`cards-grid-card-${idx}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-medium text-muted-foreground">Card {idx + 1}</span>
              <div className="flex items-center gap-1 flex-wrap justify-end">
                <button type="button" className="text-xs px-1.5 py-0.5 border rounded hover:bg-muted disabled:opacity-30" disabled={idx === 0} onClick={() => moveCard(idx, -1)} data-testid={`cards-grid-card-up-${idx}`}>▲</button>
                <button type="button" className="text-xs px-1.5 py-0.5 border rounded hover:bg-muted disabled:opacity-30" disabled={idx === cards.length - 1} onClick={() => moveCard(idx, 1)} data-testid={`cards-grid-card-down-${idx}`}>▼</button>
                <button type="button" className="text-xs px-1.5 py-0.5 border rounded hover:bg-muted disabled:opacity-30" disabled={cards.length >= MAX_CARDS_GRID_CARDS} onClick={() => duplicateCard(idx)} data-testid={`cards-grid-card-duplicate-${idx}`} title="Duplicate this card">Duplicate</button>
                <button type="button" className="text-xs px-1.5 py-0.5 border rounded hover:bg-muted disabled:opacity-30" disabled={cards.length <= 1} onClick={() => copyStyleToAll(idx)} data-testid={`cards-grid-card-copy-style-${idx}`} title="Copy this card's border, shadow, image & overlay settings to every other card">Copy style to all</button>
                <button type="button" className="text-xs px-1.5 py-0.5 border border-red-300 text-red-600 rounded hover:bg-red-50" onClick={() => removeCard(idx)} data-testid={`cards-grid-card-remove-${idx}`}>Delete</button>
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-[11px]">Background image (optional)</Label>
              {card.background_image && (
                <div className="relative">
                  <img
                    src={resolveImageUrl(card.background_image)}
                    alt="preview"
                    className="w-full max-w-xs aspect-video object-cover rounded border"
                    style={{ opacity: (card.background_image_opacity ?? 100) / 100 }}
                  />
                  <Button type="button" variant="destructive" size="sm" className="absolute top-1 right-1" onClick={() => patchCard(idx, { background_image: "" })}>Remove</Button>
                </div>
              )}
              <ImageUpload
                onUpload={(url) => patchCard(idx, { background_image: url })}
                multiple={false}
                label={card.background_image ? "Replace image" : "Upload image"}
                testId={`cards-grid-card-image-upload-${idx}`}
              />
            </div>

            <div className="space-y-1">
              <Label className="text-[11px]">Heading</Label>
              <Input value={card.heading || ""} onChange={(e) => patchCard(idx, { heading: e.target.value })} data-testid={`cards-grid-card-heading-${idx}`} />
            </div>
            <div className="space-y-1">
              <Label className="text-[11px]">Sub-heading</Label>
              <Input value={card.subheading || ""} onChange={(e) => patchCard(idx, { subheading: e.target.value })} data-testid={`cards-grid-card-subheading-${idx}`} />
            </div>
            <div className="space-y-1">
              <Label className="text-[11px]">Rich text</Label>
              <div className="border rounded-md bg-background">
                <TiptapEditor
                  value={card.rich_text || ""}
                  onChange={(html) => patchCard(idx, { rich_text: html })}
                  placeholder="Write the card's body text…"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-[11px]">CTA text</Label>
                <Input value={card.cta_text || ""} onChange={(e) => patchCard(idx, { cta_text: e.target.value })} placeholder="Learn more" data-testid={`cards-grid-card-cta-text-${idx}`} />
              </div>
              <div className="space-y-1">
                <Label className="text-[11px]">CTA URL</Label>
                <select
                  value={card.cta_url || ""}
                  onChange={(e) => patchCard(idx, { cta_url: e.target.value })}
                  className="w-full h-9 px-3 border border-input rounded text-sm bg-background"
                  data-testid={`cards-grid-card-cta-url-picker-${idx}`}
                >
                  <option value="">No link (leave blank)</option>
                  {allPages.modular?.length > 0 && (
                    <optgroup label="Modular Pages">
                      {allPages.modular.map((page) => (
                        <option key={page.composition_id} value={page.page_slug === null ? "/" : `/${page.page_slug}`}>
                          {page.page_title} ({page.page_slug === null ? "/" : `/${page.page_slug}`})
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {allPages.preset?.length > 0 && (
                    <optgroup label="Preset Pages">
                      {allPages.preset.map((page) => (
                        <option key={page.nav_id} value={page.path}>{page.title} ({page.path})</option>
                      ))}
                    </optgroup>
                  )}
                  {allPages.custom?.length > 0 && (
                    <optgroup label="Custom Pages">
                      {allPages.custom.map((page) => (
                        <option key={page.page_id} value={`/${page.slug}`}>{page.title} (/{page.slug})</option>
                      ))}
                    </optgroup>
                  )}
                </select>
                <Input
                  value={card.cta_url || ""}
                  onChange={(e) => patchCard(idx, { cta_url: e.target.value })}
                  placeholder="…or type a custom path/URL, e.g. https://example.com"
                  className="mt-1"
                  data-testid={`cards-grid-card-cta-url-${idx}`}
                />
              </div>
            </div>

            <div className="pt-2 border-t space-y-2">
              <Label className="text-[11px] font-medium">Border</Label>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-[11px]">Style</Label>
                  <select
                    value={card.border_style || "solid"}
                    onChange={(e) => patchCard(idx, { border_style: e.target.value })}
                    className="w-full h-9 px-3 border border-input rounded text-sm bg-background"
                    data-testid={`cards-grid-card-border-style-${idx}`}
                  >
                    <option value="none">None</option>
                    <option value="solid">Solid</option>
                    <option value="dashed">Dashed</option>
                    <option value="dotted">Dotted</option>
                  </select>
                </div>
                <ColorPickerInput label="Color" compact value={card.border_color || ""} onChange={(v) => patchCard(idx, { border_color: v })} />
              </div>
            </div>

            <div className="pt-2 border-t space-y-2">
              <Label className="text-[11px] font-medium">Shadow</Label>
              <div className="flex gap-1.5">
                {Object.keys(CARDS_GRID_SHADOW_PRESETS).map((key) => (
                  <Button
                    key={key}
                    type="button"
                    size="sm"
                    variant={(card.card_shadow || "none") === key ? "default" : "outline"}
                    onClick={() => patchCard(idx, { card_shadow: key })}
                    className="text-xs capitalize flex-1"
                    data-testid={`cards-grid-card-shadow-${key}-${idx}`}
                  >
                    {key}
                  </Button>
                ))}
              </div>
            </div>

            <div className="pt-2 border-t space-y-3 bg-background/60 rounded-md p-2">
              <Label className="text-[11px] font-medium">Sliders</Label>
              {card.background_image && (
                <div className="space-y-1">
                  <Label className="text-[11px]">Image opacity: {card.background_image_opacity ?? 100}%</Label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="5"
                    value={card.background_image_opacity ?? 100}
                    onChange={(e) => patchCard(idx, { background_image_opacity: parseInt(e.target.value) })}
                    className="w-full"
                    data-testid={`cards-grid-card-image-opacity-${idx}`}
                  />
                </div>
              )}
              {card.background_image && (
                <div className="space-y-1">
                  <Label className="text-[11px]">
                    Overlay darkness: {card.overlay_opacity ?? 45}%
                    <span className="text-muted-foreground font-normal"> (lower = more see-through image, text may need a darker image to stay readable)</span>
                  </Label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="5"
                    value={card.overlay_opacity ?? 45}
                    onChange={(e) => patchCard(idx, { overlay_opacity: parseInt(e.target.value) })}
                    className="w-full"
                    data-testid={`cards-grid-card-overlay-opacity-${idx}`}
                  />
                </div>
              )}
              <div className="space-y-1">
                <Label className="text-[11px]">Border width: {card.border_width ?? 1}px</Label>
                <input type="range" min="0" max="10" step="0.5" value={card.border_width ?? 1}
                  onChange={(e) => patchCard(idx, { border_width: parseFloat(e.target.value) })} className="w-full"
                  data-testid={`cards-grid-card-border-width-${idx}`} />
              </div>
              <div className="space-y-1">
                <Label className="text-[11px]">Corner radius: {card.border_radius ?? 12}px</Label>
                <input type="range" min="0" max="40" step="1" value={card.border_radius ?? 12}
                  onChange={(e) => patchCard(idx, { border_radius: parseInt(e.target.value) })} className="w-full"
                  data-testid={`cards-grid-card-border-radius-${idx}`} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Products config — form controls + live table preview of every product that
// will render on the frontend module (respects the featured-first sort).
// ---------------------------------------------------------------------------
function ProductsConfigFields({ configFields, update }) {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [jobDescNotes, setJobDescNotes] = useState("");
  const [generatingJobDesc, setGeneratingJobDesc] = useState(false);

  useEffect(() => {
    let alive = true;
    Promise.all([
      fetch(`${getApiUrl()}/products`).then((r) => r.json()).catch(() => ({ products: [] })),
      fetch(`${getApiUrl()}/product-categories`).then((r) => r.json()).catch(() => ({ categories: [] })),
    ]).then(([p, c]) => {
      if (!alive) return;
      setProducts(p.products || []);
      setCategories(c.categories || []);
    }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const generateJobDescription = async () => {
    setGeneratingJobDesc(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/cms/generate-job-description`,
        { extra_notes: jobDescNotes },
        { withCredentials: true },
      );
      const result = await pollAiJob(data.job_id);
      const html = (result?.html || "").trim();
      if (!html) {
        toast.error("AI returned no content");
        return;
      }
      update({ job_description: html });
      toast.success("Job description generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "AI generation failed");
    } finally {
      setGeneratingJobDesc(false);
    }
  };

  const limit = configFields.limit ?? 0;
  const preview = limit > 0 ? products.slice(0, limit) : products;
  const origin = typeof window !== "undefined" ? window.location.origin : "";

  const copy = (url) => {
    if (!navigator?.clipboard) return;
    navigator.clipboard.writeText(url).then(
      () => toast.success("URL copied"),
      () => toast.error("Copy failed"),
    );
  };

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        <div>
          <label className="text-xs font-medium mb-1 block">Layout <span className="text-muted-foreground font-normal">— desktop</span></label>
          <select
            value={configFields.layout || "table"}
            onChange={(e) => update({ layout: e.target.value })}
            className="w-full h-9 px-3 border border-input rounded text-sm"
            data-testid="products-config-layout"
          >
            <option value="table">Table (default) — all categories stacked</option>
            <option value="carousel">Category carousel — one category per slide with prev/next</option>
          </select>
          <p className="text-xs text-muted-foreground mt-1">
            Carousel mode shows one category at a time (ordered by category display order),
            with a stage-timeline and prev/next buttons.
          </p>
        </div>
        <div>
          <label className="text-xs font-medium mb-1 block">Layout <span className="text-muted-foreground font-normal">— mobile (≤767px)</span></label>
          <select
            value={configFields.layout_mobile || ""}
            onChange={(e) => update({ layout_mobile: e.target.value || undefined })}
            className="w-full h-9 px-3 border border-input rounded text-sm"
            data-testid="products-config-layout-mobile"
          >
            <option value="">Match desktop</option>
            <option value="table">Table — all categories stacked</option>
            <option value="carousel">Category carousel — one category per slide</option>
          </select>
          <p className="text-xs text-muted-foreground mt-1">
            Phones often benefit from a different layout — e.g. table on desktop for scannability,
            carousel on mobile to keep thumbs in play.
          </p>
        </div>
        {(configFields.layout === "carousel" || configFields.layout_mobile === "carousel") && (
          <>
            <div>
              <label className="text-xs font-medium mb-1 block">Carousel timeline <span className="text-muted-foreground font-normal">— desktop</span></label>
              <select
                value={configFields.timeline_style || "slim"}
                onChange={(e) => update({ timeline_style: e.target.value })}
                className="w-full h-9 px-3 border border-input rounded text-sm"
                data-testid="products-config-timeline-style"
              >
                <option value="slim">Slim rail — compact single row with active pill</option>
                <option value="chevrons">Numbered chevrons — classic stepper with labels under each node</option>
                <option value="hidden">Hidden — no visible timeline (screen readers still get the step)</option>
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                Slim rail is best for long shops (5+ categories). Chevrons keep every category
                name visible at a glance — nicer for short catalogues. Hidden works for single-category
                pages where the timeline would be redundant.
              </p>
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block">Carousel timeline <span className="text-muted-foreground font-normal">— mobile (≤767px)</span></label>
              <select
                value={configFields.timeline_style_mobile || ""}
                onChange={(e) => update({ timeline_style_mobile: e.target.value || undefined })}
                className="w-full h-9 px-3 border border-input rounded text-sm"
                data-testid="products-config-timeline-style-mobile"
              >
                <option value="">Match desktop</option>
                <option value="slim">Slim rail</option>
                <option value="chevrons">Numbered chevrons</option>
                <option value="hidden">Hidden</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block">
                Previous / Next button size <span className="text-muted-foreground font-normal">— % of the default</span>
              </label>
              <select
                value={configFields.nav_button_scale_pct ?? 100}
                onChange={(e) => update({ nav_button_scale_pct: parseInt(e.target.value) })}
                className="w-full h-9 px-3 border border-input rounded text-sm"
                data-testid="products-config-nav-button-scale"
              >
                {[50, 60, 70, 80, 90, 100, 110, 120, 130, 150, 175, 200].map((v) => (
                  <option key={v} value={v}>{v}%{v === 100 ? " — default" : ""}</option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                Scales the Previous and Next pill buttons around the section heading. Same value applies on desktop and mobile.
              </p>
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block">
                Height above first card <span className="text-muted-foreground font-normal">— px offset from card top</span>
              </label>
              <input
                type="number"
                min="-200"
                max="400"
                step="1"
                value={configFields.nav_button_top_offset_px ?? 0}
                onChange={(e) => update({ nav_button_top_offset_px: parseInt(e.target.value) || 0 })}
                className="w-full h-9 px-3 border border-input rounded text-sm"
                data-testid="products-config-nav-button-top-offset"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Positive values push the Previous / Next pills above the first product card. Negative values dip them into the card row.
              </p>
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block">
                Previous / Next label font size <span className="text-muted-foreground font-normal">— px, 0 = auto</span>
              </label>
              <input
                type="number"
                min="0"
                max="80"
                step="1"
                value={configFields.nav_font_size_px ?? 0}
                onChange={(e) => update({ nav_font_size_px: parseInt(e.target.value) || 0 })}
                className="w-full h-9 px-3 border border-input rounded text-sm"
                data-testid="products-config-nav-font-size"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <ColorPickerInput
                label="Previous / Next background"
                compact
                value={configFields.nav_bg_color || ""}
                onChange={(v) => update({ nav_bg_color: v })}
                testId="products-config-nav-bg-color"
              />
              <ColorPickerInput
                label="Previous / Next text"
                compact
                value={configFields.nav_text_color || ""}
                onChange={(v) => update({ nav_text_color: v })}
                testId="products-config-nav-text-color"
              />
            </div>
          </>
        )}
        <div>
          <label className="text-xs font-medium mb-1 block">
            Image size <span className="text-muted-foreground font-normal">— desktop, % of the default</span>
          </label>
          <select
            value={configFields.image_scale_pct ?? 100}
            onChange={(e) => update({ image_scale_pct: parseInt(e.target.value) })}
            className="w-full h-9 px-3 border border-input rounded text-sm"
            data-testid="products-config-image-scale"
          >
            {[10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map((v) => (
              <option key={v} value={v}>{v}%{v === 100 ? " — default" : ""}</option>
            ))}
          </select>
          <p className="text-xs text-muted-foreground mt-1">
            Shrinks the product thumbnail on both table and carousel layouts. Text stays the same.
          </p>
        </div>
        <div>
          <label className="text-xs font-medium mb-1 block">
            Image size <span className="text-muted-foreground font-normal">— mobile (≤767px)</span>
          </label>
          <select
            value={configFields.image_scale_pct_mobile ?? ""}
            onChange={(e) => update({ image_scale_pct_mobile: e.target.value === "" ? undefined : parseInt(e.target.value) })}
            className="w-full h-9 px-3 border border-input rounded text-sm"
            data-testid="products-config-image-scale-mobile"
          >
            <option value="">Match desktop</option>
            {[10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map((v) => (
              <option key={v} value={v}>{v}%</option>
            ))}
          </select>
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={configFields.show_short_description !== false}
            onChange={(e) => update({ show_short_description: e.target.checked })}
            data-testid="products-config-show-short-description"
          />
          Show short description under each product name
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={configFields.show_add_to_basket !== false}
            onChange={(e) => update({ show_add_to_basket: e.target.checked })}
            data-testid="products-config-show-add-to-basket"
          />
          Show &quot;Add to basket&quot; button (turn off for a catalogue-only, view-only shop)
        </label>
        <div>
          <label className="text-xs font-medium mb-1 block">Section title</label>
          <input type="text" value={configFields.title || ""}
            onChange={(e) => update({ title: e.target.value })}
            placeholder="Shop"
            className="w-full h-9 px-3 border border-input rounded text-sm"
            data-testid="products-config-title" />
          {/* Module title (top-of-module heading) style — same size / colour
              / weight / font pattern as the Section title further below, so
              admins get consistent typographic control at both levels. */}
          <div className="flex items-center justify-between pt-2 gap-2">
            <label className="text-[11px] font-medium text-muted-foreground">Title alignment</label>
            <div className="inline-flex rounded-md border border-input overflow-hidden" data-testid="products-config-module-title-align">
              {["left", "center", "right"].map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => update({ module_title_align: v })}
                  className={`px-3 py-1 text-xs capitalize transition-colors ${(configFields.module_title_align || "center") === v ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}
                  data-testid={`products-config-module-title-align-${v}`}
                >{v}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 pt-2">
            <div className="space-y-1">
              <label className="text-[11px] font-medium block">Title size (px)</label>
              <input
                type="number" min="10" max="96" step="1"
                value={configFields.module_title_size ?? ""}
                onChange={(e) => update({ module_title_size: e.target.value === "" ? "" : parseInt(e.target.value) })}
                placeholder="30 (default)"
                className="w-full h-9 px-3 border border-input rounded text-sm"
                data-testid="products-config-module-title-size"
              />
            </div>
            <div className="space-y-1">
              <label className="text-[11px] font-medium block">Title colour</label>
              <ColorPickerInput
                compact
                value={configFields.module_title_color || ""}
                onChange={(v) => update({ module_title_color: v })}
                data-testid="products-config-module-title-color"
              />
            </div>
            <div className="space-y-1">
              <label className="text-[11px] font-medium block">Title weight</label>
              <select
                value={configFields.module_title_weight || ""}
                onChange={(e) => update({ module_title_weight: e.target.value })}
                className="w-full h-9 px-3 border border-input rounded text-sm"
                data-testid="products-config-module-title-weight"
              >
                <option value="">Default</option>
                <option value="400">400 – Regular</option>
                <option value="500">500 – Medium</option>
                <option value="600">600 – Semi-bold</option>
                <option value="700">700 – Bold</option>
                <option value="800">800 – Extra-bold</option>
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-[11px] font-medium block">Title font</label>
              <select
                value={configFields.module_title_font_family || ""}
                onChange={(e) => update({ module_title_font_family: e.target.value })}
                className="w-full h-9 px-3 border border-input rounded text-sm"
                data-testid="products-config-module-title-font"
              >
                <option value="">Default (serif)</option>
                <option value="'Playfair Display', serif">Playfair Display (serif)</option>
                <option value="'Lora', serif">Lora (serif)</option>
                <option value="'Inter', sans-serif">Inter (sans-serif)</option>
                <option value="'Montserrat', sans-serif">Montserrat</option>
                <option value="'Poppins', sans-serif">Poppins</option>
                <option value="'Roboto', sans-serif">Roboto</option>
              </select>
            </div>
          </div>
          {/* Live swatch matches the on-page render 1:1. */}
          <div className="rounded border border-border p-3 bg-muted/30 mt-2">
            <div
              className="text-center"
              style={{
                fontSize: configFields.module_title_size ? `${configFields.module_title_size}px` : "30px",
                color: configFields.module_title_color || undefined,
                fontWeight: configFields.module_title_weight || undefined,
                fontFamily: configFields.module_title_font_family || 'ui-serif, Georgia, "Times New Roman", serif',
              }}
            >
              {configFields.title || "Sample module title"}
            </div>
          </div>
        </div>
        <div>
          <label className="text-xs font-medium mb-1 block">Subtitle</label>
          <input type="text" value={configFields.subtitle || ""}
            onChange={(e) => update({ subtitle: e.target.value })}
            placeholder="Optional short blurb under the title"
            className="w-full h-9 px-3 border border-input rounded text-sm"
            data-testid="products-config-subtitle" />
          {/* Subtitle style — same size / colour / weight / font pattern as
              the module title above, so the whole top-of-module block can
              be branded end-to-end. */}
          <div className="flex items-center justify-between pt-2 gap-2">
            <label className="text-[11px] font-medium text-muted-foreground">Subtitle alignment</label>
            <div className="inline-flex rounded-md border border-input overflow-hidden" data-testid="products-config-subtitle-align">
              {["left", "center", "right"].map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => update({ subtitle_align: v })}
                  className={`px-3 py-1 text-xs capitalize transition-colors ${(configFields.subtitle_align || "center") === v ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}
                  data-testid={`products-config-subtitle-align-${v}`}
                >{v}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 pt-2">
            <div className="space-y-1">
              <label className="text-[11px] font-medium block">Subtitle size (px)</label>
              <input
                type="number" min="8" max="48" step="1"
                value={configFields.subtitle_size ?? ""}
                onChange={(e) => update({ subtitle_size: e.target.value === "" ? "" : parseInt(e.target.value) })}
                placeholder="14 (default)"
                className="w-full h-9 px-3 border border-input rounded text-sm"
                data-testid="products-config-subtitle-size"
              />
            </div>
            <div className="space-y-1">
              <label className="text-[11px] font-medium block">Subtitle colour</label>
              <ColorPickerInput
                compact
                value={configFields.subtitle_color || ""}
                onChange={(v) => update({ subtitle_color: v })}
                data-testid="products-config-subtitle-color"
              />
            </div>
            <div className="space-y-1">
              <label className="text-[11px] font-medium block">Subtitle weight</label>
              <select
                value={configFields.subtitle_weight || ""}
                onChange={(e) => update({ subtitle_weight: e.target.value })}
                className="w-full h-9 px-3 border border-input rounded text-sm"
                data-testid="products-config-subtitle-weight"
              >
                <option value="">Default</option>
                <option value="300">300 – Light</option>
                <option value="400">400 – Regular</option>
                <option value="500">500 – Medium</option>
                <option value="600">600 – Semi-bold</option>
                <option value="700">700 – Bold</option>
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-[11px] font-medium block">Subtitle font</label>
              <select
                value={configFields.subtitle_font_family || ""}
                onChange={(e) => update({ subtitle_font_family: e.target.value })}
                className="w-full h-9 px-3 border border-input rounded text-sm"
                data-testid="products-config-subtitle-font"
              >
                <option value="">Default (body)</option>
                <option value="'Playfair Display', serif">Playfair Display (serif)</option>
                <option value="'Lora', serif">Lora (serif)</option>
                <option value="'Inter', sans-serif">Inter (sans-serif)</option>
                <option value="'Montserrat', sans-serif">Montserrat</option>
                <option value="'Poppins', sans-serif">Poppins</option>
                <option value="'Roboto', sans-serif">Roboto</option>
              </select>
            </div>
          </div>
          {/* Live swatch matches the on-page render 1:1. */}
          <div className="rounded border border-border p-3 bg-muted/30 mt-2">
            <div
              className="text-center"
              style={{
                fontSize: configFields.subtitle_size ? `${configFields.subtitle_size}px` : "14px",
                color: configFields.subtitle_color || "hsl(var(--muted-foreground))",
                fontWeight: configFields.subtitle_weight || undefined,
                fontFamily: configFields.subtitle_font_family || "inherit",
              }}
            >
              {configFields.subtitle || "Sample subtitle line"}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium mb-1 block">Columns</label>
            <select value={configFields.columns || 3}
              onChange={(e) => update({ columns: parseInt(e.target.value) })}
              className="w-full h-9 px-3 border border-input rounded text-sm"
              disabled={(configFields.card_width || 0) > 0}
              data-testid="products-config-columns">
              <option value={1}>1</option>
              <option value={2}>2</option>
              <option value={3}>3</option>
              <option value={4}>4</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-medium mb-1 block">Limit (0 = show all)</label>
            <input type="number" min="0" value={configFields.limit ?? 0}
              onChange={(e) => update({ limit: parseInt(e.target.value) || 0 })}
              className="w-full h-9 px-3 border border-input rounded text-sm"
              data-testid="products-config-limit" />
          </div>
        </div>
        <div>
          <label className="text-xs font-medium mb-1 block">
            Card min width (px) <span className="text-muted-foreground font-normal">— 0 = use Columns instead</span>
          </label>
          <input
            type="number"
            min="0"
            max="800"
            step="10"
            value={configFields.card_width ?? 0}
            onChange={(e) => update({ card_width: parseInt(e.target.value) || 0 })}
            placeholder="e.g. 280"
            className="w-full h-9 px-3 border border-input rounded text-sm"
            data-testid="products-config-card-width"
          />
          <p className="text-xs text-muted-foreground mt-1">
            When set, cards flow at this minimum width and columns adapt to the container automatically. Card height follows the image height + content, no cropping.
          </p>
        </div>

        <div className="flex items-center justify-between gap-3">
          <label className="text-xs font-medium">
            Table border colour <span className="text-muted-foreground font-normal">(table layout only)</span>
          </label>
          <ColorPickerInput
            compact
            value={configFields.table_border_color || ""}
            onChange={(v) => update({ table_border_color: v })}
            testId="products-config-table-border-color"
          />
        </div>

        {/* --- Section title style (gallery/carousel category name) ---
            Fine-grained control over the category heading rendered above
            each carousel slide (Freebies / Property Listing / etc.). Falls
            back to Site Styles defaults when values are left empty. */}
        <div className="pt-2 border-t space-y-3">
          <h4 className="font-medium text-sm">Section Title (category name) style</h4>
          <div className="flex items-center justify-between gap-2">
            <label className="text-[11px] font-medium text-muted-foreground">Section title alignment</label>
            <div className="inline-flex rounded-md border border-input overflow-hidden" data-testid="products-config-section-title-align">
              {["left", "center", "right"].map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => update({ section_title_align: v })}
                  className={`px-3 py-1 text-xs capitalize transition-colors ${(configFields.section_title_align || "center") === v ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}
                  data-testid={`products-config-section-title-align-${v}`}
                >{v}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-medium block">Size (px)</label>
              <input
                type="number"
                min="10" max="80" step="1"
                value={configFields.section_title_size ?? ""}
                onChange={(e) => update({ section_title_size: e.target.value === "" ? "" : parseInt(e.target.value) })}
                placeholder="24 (default)"
                className="w-full h-9 px-3 border border-input rounded text-sm"
                data-testid="products-config-section-title-size"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium block">Colour</label>
              <ColorPickerInput
                compact
                value={configFields.section_title_color || ""}
                onChange={(v) => update({ section_title_color: v })}
                data-testid="products-config-section-title-color"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium block">Weight</label>
              <select
                value={configFields.section_title_weight || ""}
                onChange={(e) => update({ section_title_weight: e.target.value })}
                className="w-full h-9 px-3 border border-input rounded text-sm"
                data-testid="products-config-section-title-weight"
              >
                <option value="">Default</option>
                <option value="400">400 – Regular</option>
                <option value="500">500 – Medium</option>
                <option value="600">600 – Semi-bold</option>
                <option value="700">700 – Bold</option>
                <option value="800">800 – Extra-bold</option>
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium block">Font family</label>
              <select
                value={configFields.section_title_font_family || ""}
                onChange={(e) => update({ section_title_font_family: e.target.value })}
                className="w-full h-9 px-3 border border-input rounded text-sm"
                data-testid="products-config-section-title-font"
              >
                <option value="">Default (serif)</option>
                <option value="'Playfair Display', serif">Playfair Display (serif)</option>
                <option value="'Lora', serif">Lora (serif)</option>
                <option value="'Inter', sans-serif">Inter (sans-serif)</option>
                <option value="'Montserrat', sans-serif">Montserrat</option>
                <option value="'Poppins', sans-serif">Poppins</option>
                <option value="'Roboto', sans-serif">Roboto</option>
              </select>
            </div>
          </div>
          {/* Live swatch matches the on-page render 1:1. */}
          <div className="rounded border border-border p-3 bg-muted/30">
            <div
              className="text-center"
              style={{
                fontSize: configFields.section_title_size ? `${configFields.section_title_size}px` : "24px",
                color: configFields.section_title_color || undefined,
                fontWeight: configFields.section_title_weight || undefined,
                fontFamily: configFields.section_title_font_family || 'ui-serif, Georgia, "Times New Roman", serif',
              }}
            >
              {configFields.title || "Sample category name"}
            </div>
          </div>
        </div>

        {/* --- Product name (heading) alignment — applies to every
            product's name in both the table/list rows and the
            grid/carousel cards. --- */}
        <div className="pt-2 border-t">
          <div className="flex items-center justify-between gap-2">
            <label className="text-[11px] font-medium text-muted-foreground">Product name alignment</label>
            <div className="inline-flex rounded-md border border-input overflow-hidden" data-testid="products-config-product-name-align">
              {["left", "center", "right"].map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => update({ product_name_align: v })}
                  className={`px-3 py-1 text-xs capitalize transition-colors ${(configFields.product_name_align || "left") === v ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}
                  data-testid={`products-config-product-name-align-${v}`}
                >{v}</button>
              ))}
            </div>
          </div>
        </div>

        {/* --- Default category + example URLs ------------------------- */}
        <div className="pt-2 border-t space-y-3">
          <div>
            <label className="text-xs font-medium mb-1 block">Default category (optional)</label>
            <select
              value={configFields.default_category || ""}
              onChange={(e) => update({ default_category: e.target.value })}
              className="w-full h-9 px-3 border border-input rounded text-sm"
              data-testid="products-config-default-category"
            >
              <option value="">— All categories —</option>
              {categories.map((c) => (
                <option key={c.category_id} value={c.slug}>{c.name}</option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground mt-1">
              When set, this module opens pre-filtered to that category. A{" "}
              <code className="bg-muted/60 px-1 rounded">?category=</code> URL still overrides it.
            </p>
          </div>

          <label className="flex items-start gap-3 rounded-md border border-input p-3 cursor-pointer">
            <input
              type="checkbox"
              checked={configFields.show_category_line === true}
              onChange={(e) => update({ show_category_line: e.target.checked })}
              className="mt-0.5 h-4 w-4"
              data-testid="products-config-show-category-line"
            />
            <span className="text-sm">
              <span className="font-medium">Show &quot;Showing category:&quot; line</span>
              <span className="block text-xs text-muted-foreground mt-0.5">
                Displays the active filter pill above the product grid. Off by default — turn on only if the active category isn't already obvious from the page title or hero banner.
              </span>
            </span>
          </label>

          <label className="flex items-start gap-3 rounded-md border border-input p-3 cursor-pointer">
            <input
              type="checkbox"
              checked={configFields.show_description_preview === true}
              onChange={(e) => update({ show_description_preview: e.target.checked })}
              className="mt-0.5 h-4 w-4"
              data-testid="products-config-show-description-preview"
            />
            <span className="text-sm">
              <span className="font-medium">Show description preview</span>
              <span className="block text-xs text-muted-foreground mt-0.5">
                Shows the first 5 lines of the rich description inline, with a <em>more…</em> link that opens the full detail view.
              </span>
            </span>
          </label>

          <label className="flex items-start gap-3 rounded-md border border-input p-3 cursor-pointer">
            <input
              type="checkbox"
              checked={configFields.show_full_description === true}
              onChange={(e) => update({ show_full_description: e.target.checked })}
              className="mt-0.5 h-4 w-4"
              data-testid="products-config-show-full-description"
            />
            <span className="text-sm">
              <span className="font-medium">Show full (long) description</span>
              <span className="block text-xs text-muted-foreground mt-0.5">
                Shows the entire rich description inline under the product name — no truncation, no <em>more…</em> link. Overrides "Show description preview" when both are on.
              </span>
            </span>
          </label>

          <label className="flex items-start gap-3 rounded-md border border-input p-3 cursor-pointer">
            <input
              type="checkbox"
              checked={configFields.show_supplier === true}
              onChange={(e) => update({ show_supplier: e.target.checked })}
              className="mt-0.5 h-4 w-4"
              data-testid="products-config-show-supplier"
            />
            <span className="text-sm">
              <span className="font-medium">Show supplier name</span>
              <span className="block text-xs text-muted-foreground mt-0.5">
                Displays the sourcing supplier (if any) under the short description.
              </span>
            </span>
          </label>

          <div className="rounded-md border border-blue-200 bg-blue-50/60 p-3 space-y-2">
            <div className="text-sm font-medium text-blue-900">
              Filter products by category via URL
            </div>
            <p className="text-xs text-blue-900/70">
              Append <code className="bg-white/70 px-1 rounded">?category=&lt;slug&gt;</code> to
              any page hosting this module to scope the grid to one category.
              Handy for marketing links.
            </p>
            <p className="text-xs text-blue-900/70">
              Add <code className="bg-white/70 px-1 rounded">&amp;addToBasket=yes</code> to
              also auto-add every product in that category straight to the
              shopper's basket on page load — e.g.{" "}
              <code className="bg-white/70 px-1 rounded">?category=marketing&amp;addToBasket=yes</code>.
              Runs once per category per browser session.
            </p>
            {categories.length === 0 ? (
              <p className="text-xs text-blue-900/70 italic">
                No categories yet — create one in <strong>Admin → Categories</strong>.
              </p>
            ) : (
              <div className="space-y-1">
                {categories.map((c) => {
                  const rel = `/products?category=${c.slug}`;
                  const abs = `${origin}${rel}`;
                  return (
                    <div
                      key={c.category_id}
                      className="flex items-center justify-between gap-2 rounded bg-white/70 border border-blue-100 px-2 py-1.5 text-xs"
                      data-testid={`products-instance-example-${c.slug}`}
                    >
                      <div className="truncate">
                        <span className="text-blue-900/70">{c.name}</span>
                        <code className="ml-2 text-blue-900">{rel}</code>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          type="button"
                          onClick={() => copy(abs)}
                          className="p-1 rounded hover:bg-blue-100 text-blue-900"
                          title="Copy full URL"
                          data-testid={`products-instance-example-copy-${c.slug}`}
                        >
                          <Copy className="h-3.5 w-3.5" />
                        </button>
                        <a
                          href={rel}
                          target="_blank"
                          rel="noreferrer"
                          className="p-1 rounded hover:bg-blue-100 text-blue-900"
                          title="Open filtered shop"
                          data-testid={`products-instance-example-open-${c.slug}`}
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* --- Job description (recruit preferred suppliers) --------------- */}
      <div className="space-y-2 rounded-md border border-primary/30 bg-primary/[0.03] p-3">
        <div className="flex items-center justify-between gap-2">
          <label className="text-sm font-semibold flex items-center gap-1.5">
            <Sparkles className="h-3.5 w-3.5 text-primary" />
            Job description
          </label>
          <Button
            type="button"
            size="sm"
            onClick={generateJobDescription}
            disabled={generatingJobDesc}
            className="gap-1.5"
            data-testid="products-config-generate-job-description"
          >
            {generatingJobDesc ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Sparkles className="h-3.5 w-3.5" />
            )}
            Generate with AI
          </Button>
        </div>
        <Textarea
          value={jobDescNotes}
          onChange={(e) => setJobDescNotes(e.target.value)}
          placeholder="Optional notes for the AI, e.g. 'especially need suppliers in Kent'"
          rows={2}
          data-testid="products-config-job-description-notes"
        />
        <p className="text-[11px] text-muted-foreground">
          Generates a &quot;Become a Preferred Supplier&quot; description that invites
          companies and individuals to apply for work in their area, grounded
          in this site&apos;s real service categories and product catalogue.
        </p>
        <div className="border rounded-md bg-background">
          <TiptapEditor
            value={configFields.job_description || ""}
            onChange={(html) => update({ job_description: html })}
            placeholder="Click Generate with AI, or write your own job description here…"
          />
        </div>
      </div>

      <div className="border border-border rounded overflow-hidden bg-background" data-testid="products-config-preview">
        <div className="px-3 py-2 border-b bg-muted/40 text-xs font-medium flex items-center justify-between">
          <span>Live products preview ({preview.length}{limit > 0 && ` of ${products.length}`})</span>
          <a href="/admin?tab=products" target="_blank" rel="noreferrer" className="text-primary hover:underline">Manage catalogue →</a>
        </div>
        {loading ? (
          <div className="p-6 text-center text-xs text-muted-foreground">Loading products…</div>
        ) : preview.length === 0 ? (
          <div className="p-6 text-center text-xs text-muted-foreground">
            No live products yet — add one in <strong>Admin → Products</strong>.
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="text-left text-muted-foreground">
              <tr>
                <th className="px-3 py-2 w-8"></th>
                <th className="px-3 py-2">Image</th>
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Price</th>
              </tr>
            </thead>
            <tbody>
              {preview.map((p, idx) => (
                <tr key={p.product_id} className="border-t border-border">
                  <td className="px-3 py-2 text-muted-foreground">{idx + 1}</td>
                  <td className="px-3 py-2">
                    {p.image_url
                      ? <img src={p.image_url} alt={p.name} className="h-8 w-8 object-cover rounded" />
                      : <div className="h-8 w-8 bg-muted rounded" />}
                  </td>
                  <td className="px-3 py-2 font-medium flex items-center gap-1">
                    {p.featured && <span title="Featured" className="text-amber-500">★</span>}
                    {p.name}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">{p.currency || "GBP"} {Number(p.price || 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/**
 * Reusable Heading + CTA configuration block for property-listing modules.
 * Manages heading text/style and an optional configurable CTA button with a
 * dropdown that links to any page in the site (modular, preset, custom).
 */
function HeadingCtaFields({ configFields, update, allPages }) {
  return (
    <>
      <div className="p-3 bg-slate-50 rounded-lg border space-y-3">
        <h4 className="font-semibold text-sm">Section Heading</h4>
        <div className="space-y-2">
          <Label>Heading Text</Label>
          <Input
            placeholder="All Properties"
            value={configFields.heading_text || ""}
            onChange={(e) => update({ heading_text: e.target.value })}
            data-testid="heading-text-input"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label>Font Family</Label>
            <select
              value={configFields.heading_font_family || "inherit"}
              onChange={(e) => update({ heading_font_family: e.target.value })}
              className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="inherit">Default (Site Body)</option>
              <option value="'Playfair Display', serif">Playfair Display</option>
              <option value="'Inter', sans-serif">Inter</option>
              <option value="'Montserrat', sans-serif">Montserrat</option>
              <option value="'Poppins', sans-serif">Poppins</option>
              <option value="'Lora', serif">Lora</option>
              <option value="'Roboto', sans-serif">Roboto</option>
              <option value="'Open Sans', sans-serif">Open Sans</option>
              <option value="'Merriweather', serif">Merriweather</option>
              <option value="'Source Sans Pro', sans-serif">Source Sans Pro</option>
            </select>
          </div>
          <div className="space-y-2">
            <Label>Font Weight</Label>
            <select
              value={configFields.heading_font_weight || "700"}
              onChange={(e) => update({ heading_font_weight: e.target.value })}
              className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="300">Light (300)</option>
              <option value="400">Regular (400)</option>
              <option value="500">Medium (500)</option>
              <option value="600">Semi Bold (600)</option>
              <option value="700">Bold (700)</option>
              <option value="800">Extra Bold (800)</option>
              <option value="900">Black (900)</option>
            </select>
          </div>
          <div className="space-y-2">
            <Label>Font Style</Label>
            <select
              value={configFields.heading_font_style || "normal"}
              onChange={(e) => update({ heading_font_style: e.target.value })}
              className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="normal">Normal</option>
              <option value="italic">Italic</option>
            </select>
          </div>
          <div className="space-y-2">
            <Label>Text Transform</Label>
            <select
              value={configFields.heading_text_transform || "none"}
              onChange={(e) => update({ heading_text_transform: e.target.value })}
              className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="none">None</option>
              <option value="uppercase">UPPERCASE</option>
              <option value="lowercase">lowercase</option>
              <option value="capitalize">Capitalize</option>
            </select>
          </div>
          <ColorPickerInput
            label="Heading Color"
            compact
            value={configFields.heading_color || "#1a1a1a"}
            onChange={(v) => update({ heading_color: v })}
          />
          <div className="space-y-2">
            <Label>Justification</Label>
            <select
              data-testid="heading-align-select"
              value={configFields.heading_align || "center"}
              onChange={(e) => update({ heading_align: e.target.value })}
              className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="left">Left</option>
              <option value="center">Center</option>
              <option value="right">Right</option>
              <option value="justify">Justify</option>
            </select>
          </div>
        </div>
        <div className="space-y-2">
          <Label>Heading size (H1 – H6)</Label>
          <HeadingLevelSelect
            value={configFields.heading_font_size}
            onChange={(lvl) => update({ heading_font_size: lvl })}
            testId="hero-heading-level"
          />
        </div>

        <div className="space-y-2 pt-2 border-t">
          <Label>Subheading (optional)</Label>
          <Input
            placeholder="Browse all our available properties"
            value={configFields.subheading_text || ""}
            onChange={(e) => update({ subheading_text: e.target.value })}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <ColorPickerInput
            label="Subheading Color"
            compact
            value={configFields.subheading_color || ""}
            onChange={(v) => update({ subheading_color: v })}
          />
          <div className="space-y-2">
            <Label>Subheading size (H1 – H6)</Label>
            <HeadingLevelSelect
              value={configFields.subheading_font_size}
              onChange={(lvl) => update({ subheading_font_size: lvl })}
              testId="hero-subheading-level"
            />
          </div>
        </div>
        <TextStyleControls
          label="Subheading Style"
          prefix="subheading_"
          config={configFields}
          update={update}
          preview={configFields.subheading_text || "Browse all our available properties"}
          compact
        />
      </div>

      <div className="p-3 bg-slate-50 rounded-lg border space-y-3">
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="cta_enabled_toggle"
            checked={!!configFields.cta_enabled}
            onChange={(e) => update({ cta_enabled: e.target.checked })}
            data-testid="cta-enabled-checkbox"
          />
          <Label htmlFor="cta_enabled_toggle" className="font-semibold text-sm">
            Configurable CTA Button
          </Label>
        </div>
        {configFields.cta_enabled && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>CTA Text</Label>
                <Input
                  placeholder="View All Properties"
                  value={configFields.cta_text || ""}
                  onChange={(e) => update({ cta_text: e.target.value })}
                  data-testid="cta-text-input"
                />
              </div>
              <div className="space-y-2">
                <Label>CTA Position</Label>
                <select
                  data-testid="cta-position-select"
                  value={configFields.cta_position || "top"}
                  onChange={(e) => update({ cta_position: e.target.value })}
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="top">Above property list</option>
                  <option value="bottom">Below property list</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label>CTA Alignment</Label>
                <select
                  value={configFields.cta_align || "center"}
                  onChange={(e) => update({ cta_align: e.target.value })}
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="left">Left</option>
                  <option value="center">Center</option>
                  <option value="right">Right</option>
                </select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Link To Page</Label>
              <select
                data-testid="cta-link-page-select"
                value={configFields.cta_link || ""}
                onChange={(e) => update({ cta_link: e.target.value })}
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="">Select a page…</option>
                {allPages.modular?.length > 0 && (
                  <optgroup label="Modular Pages">
                    {allPages.modular.map((p) => (
                      <option
                        key={p.composition_id}
                        value={p.page_slug === null ? "/" : (p.page_slug ? `/${p.page_slug}` : "/")}
                      >
                        {p.page_title} ({p.page_slug === null ? "/" : `/${p.page_slug}`})
                      </option>
                    ))}
                  </optgroup>
                )}
                {allPages.preset?.length > 0 && (
                  <optgroup label="Preset Pages">
                    {allPages.preset.map((p) => (
                      <option key={p.nav_id} value={p.path}>
                        {p.title} ({p.path})
                      </option>
                    ))}
                  </optgroup>
                )}
                {allPages.custom?.length > 0 && (
                  <optgroup label="Custom Pages">
                    {allPages.custom.map((p) => (
                      <option key={p.page_id} value={`/${p.slug}`}>
                        {p.title} (/{p.slug})
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <ColorPickerInput
                label="CTA Background"
                compact
                value={configFields.cta_bg_color || "#2f4f4f"}
                onChange={(v) => update({ cta_bg_color: v })}
              />
              <ColorPickerInput
                label="CTA Text Color"
                compact
                value={configFields.cta_text_color || "#ffffff"}
                onChange={(v) => update({ cta_text_color: v })}
              />
            </div>
            <div className="space-y-2">
              <Label>Corner Radius: {configFields.cta_border_radius || 8}px</Label>
              <input
                type="range"
                min="0"
                max="40"
                value={configFields.cta_border_radius || 8}
                onChange={(e) => update({ cta_border_radius: parseInt(e.target.value) })}
                className="w-full"
              />
            </div>
            <TextStyleControls
              label="CTA Button Text Style"
              prefix="cta_"
              config={configFields}
              update={update}
              preview={configFields.cta_text || "View All Properties"}
              compact
            />
          </div>
        )}
      </div>
    </>
  );
}
